/**
 * Smart capture — ARCH-node-mcp (Core layer)
 * Chain: FEAT-MCP-001 (MCP Server), ARCH-flow-smart-capture
 * Rules: SOS-016 (governed draft-first), SOS-015 (unique IDs within scope)
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, kernelCall, getWorkspaceContext, refreshWorkspaceGovernanceMode, requireWriteAccess, recordSessionActivity, getAgentSessionId, cacheScope, runWithToolContext } from "../client.js";
import { isGatewayTimeout, buildCaptureOutcomeUnknownResult } from "../lib/captureTimeoutOutcome.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { ScopedCache, COLLECTION_CACHE_TTL_MS } from "../lib/scopedCache.js";
import {
  trackCaptureClassifierAutoRouted,
  trackCaptureClassifierEvaluated,
  trackCaptureClassifierFallback,
  trackCaptureQualityHints,
  trackCaptureRelationSuggestions,
  trackQualityVerdict,
  trackChainEntryCommitted,
  trackCollectionClassified,
  trackFieldGuidanceApplied,
  trackClassifierDivergence,
  type ClassifierReasonCategory,
} from "../analytics.js";
import { extractPreview } from "./knowledge-helpers.js";
// WP-465 surface parity (TEN-2458): import from coherency-refusal.ts (not knowledge.ts) to avoid circular dep.
import { coherencyRefusedResult } from "./coherency-refusal.js";
import { resolveGapsForEntry } from "../gap-store.js";
import {
  CLASSIFIABLE_COLLECTIONS,
  type ClassifiableCollectionSlug,
} from "./smart-capture-routing.js";
import { resolveCollection, type ResolvedCollection } from "../lib/resolveCollection.js";
import { thinWrapper, success, failure, parseOrFail, type NextAction, type EnvelopeError, type CaptureContract, type ToolResult } from "../envelope.js";
import { FIELD_TYPE_DEFAULTS, type FieldType } from "../lib/fieldTypes.js";
import { formatFieldGuidance } from "../lib/formatFieldGuidance.js";
import { inferSourceDate } from "../lib/inferSourceDate.js";
import {
  getCollections,
  getCollectionBySlug,
  isGoverned,
  isHubCollection,
  type CollectionDoc,
} from "../lib/collectionCache.js";
import { STOP_WORDS, tokenizeText } from "../lib/textMatch.js";
import { buildBatchPreviewFromResults, buildBatchEntryFailure, describeBatchEntryOutcome, splitBatchNotOk, unknownOutcomeSummaryNote, renderOutcomeUnknownSection, type BatchTimeoutOutcome } from "../lib/batchCaptureOutput.js"; // WP-575 review (P1, TEN-1126) — outcome-unknown cohort re-exported from lib/batchTimeoutCohort.ts.
import { isPreviewCreateEntryResult, extractPreviewWouldBlock, resolveEffectiveCollection } from "../lib/createEntryPreviewResult.js";
import { isEmptyValue, isNonEmptyValue, canonicalizeSelects } from "../lib/entryDataNormalization.js";
import { applyBatchInlineRelations } from "../lib/batchInlineRelations.js";
import { buildCaptureForRealNextAction } from "../lib/captureForRealAction.js";
import { verdictQualityPhrase, formatRubricCoaching, formatRubricVerdictSection } from "../lib/rubricFormatting.js";
import { captureSinglePreviewOutputSchema } from "../lib/captureSinglePreviewOutput.js";
import { createBatchPreviewDuplicateTracker, checkBatchPreviewDuplicate, reconcileBatchPreviewDuplicate } from "../lib/batchPreviewDuplicates.js";

export {
  CLASSIFIER_AUTO_ROUTE_THRESHOLD,
  CLASSIFIABLE_COLLECTIONS,
  isClassificationAmbiguous,
  type ClassifiableCollectionSlug,
  type ClassificationResult,
} from "./smart-capture-routing.js";

// ── Collection Workflow Profiles ────────────────────────────────────────────

interface FieldDefault {
  key: string;
  value: unknown | "today" | "infer";
}

interface CollectionProfile {
  governedDraft: boolean;
  defaults: FieldDefault[];
  descriptionField: string;
  recommendedRelationTypes: string[];
  inferField?: (ctx: CaptureContext) => Record<string, unknown>;
}

interface CaptureContext {
  collection: string;
  name: string;
  description: string;
  context?: string;
  data: Record<string, unknown>;
  entryId: string;
  canonicalKey?: string;
  linksCreated: LinkResult[];
  linksSuggested: LinkSuggestion[];
  collectionFields: Array<{ key: string; type: string; required?: boolean; options?: string[]; label?: string; writingGuidance?: string; writingExamples?: string[]; minLength?: number; maxLength?: number }>;
}

interface LinkResult {
  targetEntryId: string;
  targetName: string;
  targetCollection: string;
  relationType: string;
  linkReason?: string;
}

interface LinkSuggestion {
  entryId?: string;
  name: string;
  collection: string;
  reason: string;
  preview: string;
}

// normalizeMatchText/tokenizeMatchText/scoreOptionMatch/inferFieldOption were dead
// private helpers with zero callers anywhere in this file — deleted (STD-2 / DEC-1504
// module-health cleanup), not moved.

// ── Strategy Category Inference (FEAT-128) ──────────────────────────────────
// WP-488 Slice 4 (FEAT-1378, TEN-2733): moved to kernel/strategy/inferCategory.ts,
// served over the gateway as `chain.classifyStrategyCategory` — no connector-local
// copy. This function had zero production callers in this file at the time of the
// move (re-grounded finding, not assumed from the WP-D spec's original pointers); see
// kernel/strategy/inferCategory.ts's header and the PR body for the full note. A
// future caller reaches the verdict via
// `kernelQuery<GatewayRouteReturnByName<'chain.classifyStrategyCategory'>>
// ('chain.classifyStrategyCategory', { name, description })`.

// ── Collection Profiles ─────────────────────────────────────────────────────
// BR-183 / E4.2: Runtime profiles are derived from collection documents. The
// generic fallback intentionally avoids slug-specific defaults, relation
// policies, and inference because collection docs do not expose those sources.

const GENERIC_PROFILE: CollectionProfile = {
  governedDraft: false,
  descriptionField: "description",
  defaults: [],
  recommendedRelationTypes: ["related_to", "references"],
};

/**
 * E4.2: Build a runtime profile from collection metadata. Do not infer
 * relation policies, default values, or field-population policies from slug.
 *
 * WP-480 S1 (FEAT-1358): quality is judged ONCE, server-side (STD-101 / DEC-32
 * thin wrapper), so this profile carries no client scorecard — it keeps only
 * description-field resolution and relation defaults.
 */
async function buildRuntimeProfile(slug: string): Promise<CollectionProfile> {
  const col = await getCollectionBySlug(slug);
  if (!col) return GENERIC_PROFILE;

  const fields = col.fields ?? [];
  const layoutDescriptionField = fields.find(
    (field) => field.zone === "body" || field.displayHint === "textarea" || field.displayHint === "section",
  )?.key;
  const descriptionField = col.descriptionFieldKey || layoutDescriptionField || "description";

  return {
    governedDraft: col.governed === true,
    descriptionField,
    defaults: [],
    recommendedRelationTypes: GENERIC_PROFILE.recommendedRelationTypes,
  };
}

/**
 * Per-scope profile cache (keyed by collection slug within a scope).
 * Note: TTL is stamped when a scope's Map is first created; later map.set adds
 * entries without refreshing it, so all profiles in a scope expire ≤TTL after the
 * first one. Fail-safe toward freshness (can only expire sooner); staleness
 * ceiling stays COLLECTION_CACHE_TTL_MS.
 */
const profileCache = new ScopedCache<Map<string, CollectionProfile>>(COLLECTION_CACHE_TTL_MS);

/**
 * Get a collection's profile for the current scope, cached with a short TTL.
 */
export async function getProfile(slug: string): Promise<CollectionProfile> {
  const scope = cacheScope();
  let map = profileCache.get(scope);
  if (!map) {
    map = new Map();
    profileCache.set(scope, map);
  }
  const cached = map.get(slug);
  if (cached) return cached;
  const profile = await buildRuntimeProfile(slug);
  map.set(slug, profile);
  return profile;
}

/** Reset all profile caches — test hook + full flush. */
export function resetProfileCache(): void {
  profileCache.clear();
}

/**
 * Drop the current scope's profile cache. Profiles derive their descriptionField and
 * quality criteria from collection fields, so a collection write in this scope must drop
 * them too — otherwise the next capture uses the pre-write profile until the TTL expires.
 */
export function invalidateProfileCacheForScope(): void {
  profileCache.delete(cacheScope());
}

// ── Auto-Linking Logic ──────────────────────────────────────────────────────

function extractSearchTerms(name: string, description: string): string {
  const text = `${name} ${description}`;
  return text
    .replace(/[^\w\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 3)
    .slice(0, 8)
    .join(" ");
}

/**
 * Positional result + jobs-request shape for `chain.scoreLinkCandidates` (Lane C S4).
 * Mirror the served route's request/response shape (convex/agentKnowledge/relations.ts).
 * Hand-declared here rather than imported from kernel/ (mcp-server cannot import kernel/
 * directly — no build edge, same reasoning as chain-classifier.ts's hand-declared
 * GovernanceFunction/GovernanceRelationEdge pre-S3a) and not (yet) re-exported from
 * @productbrain/kernel-client either — this route's types are net-new to that package's
 * surface, out of S4's scope (S3a's precedent is the addition path if a future slice
 * wants type-only re-export here instead of a hand-declared mirror).
 *
 * PR #392 round 1 (Copilot + Codex, confirmed): candidates carry ONLY {name,
 * collectionId} on the wire — `data`/`entryId` were never read by the scoring formula
 * and risked tripping convex/http.ts's validateArgLengths (10KB-per-string, recursive)
 * for a large entry payload, failing the whole scoring call. Callers keep their own full
 * candidate objects locally and re-associate via the positional `index` — nothing lost.
 *
 * PR #392 round 2 (Codex, confirmed): `collMap`/`hubSlugs` are HOISTED out of
 * `LinkScoreJob` — they're the SAME workspace-wide caches for every job in one capture
 * flow, so sending them per-job (round 1's shape) meant an N-job batch request repeated
 * the identical collMap/hubSlugs JSON N times. Now sent once, at the request's top
 * level (see `scoreEntryJobsChunk` below).
 */
interface LinkScoreResult {
  index: number;
  score: number;
  reason: string;
}

/** One unit of scoring work — see kernel/relations/scoreLinkCandidates.ts's LinkScoreJob.
 * `collMap`/`hubSlugs` are NOT here — hoisted to the request's top level (round 2). */
interface LinkScoreJob {
  candidates: Array<{ name: string; collectionId: string }>;
  sourceName: string;
  sourceDescription: string;
  sourceCollection: string;
}

// BET-281: hub slugs get higher link-confidence. Per-scope + TTL (TEN-2415).
const hubCache = new ScopedCache<Set<string>>(COLLECTION_CACHE_TTL_MS);

/** Resolve (and cache) the current scope's hub-collection slug set. */
export async function populateHubCache(): Promise<Set<string>> {
  const scope = cacheScope();
  const hit = hubCache.get(scope);
  if (hit) return hit;
  const cols = await getCollections();
  const set = new Set(
    cols
      .filter((c) => c.governanceRole === "steering" || c.thinkingLayer === "strategy")
      .map((c) => c.slug),
  );
  hubCache.set(scope, set);
  return set;
}

/** Reset all hub caches — test hook + full flush. */
export function resetHubCache(): void {
  hubCache.clear();
}

/**
 * Drop the current scope's hub-slug set. The set derives from collection governanceRole /
 * thinkingLayer, so a collection write in this scope must drop it too — otherwise link
 * confidence keeps scoring against the pre-write hub set until the TTL expires.
 */
export function invalidateHubCacheForScope(): void {
  hubCache.delete(cacheScope());
}

/**
 * Lane C S4 (TEN-2733 pattern extension): builds ONE scoring job for the batched
 * served-kernel-verdict call that replaced this file's former local link-scoring
 * function (a connector-resident scorer with no kernel/convex counterpart — the same
 * architectural gap the classifier migrations already closed elsewhere). Deliberately
 * does NOT call `kernelQuery` itself — each call site below invokes the gateway route
 * directly (not through a shared wrapper) so the route-name string literal appears at
 * the real call site(s), never hidden behind a single shared indirection point (grep-
 * based doneWhen — see PR body). Candidates are trimmed to `{name, collectionId}` here
 * — see the `LinkScoreJob` doc comment above for why `data`/`entryId` never travel over
 * the wire. `collMap`/`hubSlugs` are NOT built here (round 2) — they're THIS connector's
 * own already-fetched/cached data, hoisted to the request's top level by each call site.
 */
function buildScoreLinkCandidatesJob(
  candidates: ReadonlyArray<{ name: string; collectionId: string }>,
  sourceName: string,
  sourceDescription: string,
  sourceCollection: string,
): LinkScoreJob {
  return {
    candidates: candidates.map((c) => ({ name: c.name, collectionId: c.collectionId })),
    sourceName,
    sourceDescription,
    sourceCollection,
  };
}

/**
 * PR #392 round 2 (Codex, confirmed): batch-capture's scoring jobs are processed in
 * CHUNKS, not one call for the WHOLE batch. Two independent bounds, whichever hits
 * first closes the current chunk:
 * - COUNT: at most this many entries' jobs per `chain.scoreLinkCandidates` call — keeps
 *   a 50-entry batch at ≤5 scoring calls (was: 1 unbounded call before this fix, 50
 *   calls before round 1's batching fix).
 * - BYTES: a conservative `JSON.stringify` length estimate of the accumulated chunk's
 *   jobs, so a chunk of entries with unusually large candidate names/counts still stays
 *   safely under Convex's ~1MB per-call argument ceiling (this margin, not the ceiling
 *   itself — leaves room for the request's other fields and the wire envelope).
 * CHUNKING ALSO RESTORES WRITE INTERLEAVING: each chunk's relation writes (`finish`)
 * run to completion BEFORE the next chunk's entries even start their `createEntry` —
 * see the batch-capture loop below — so auto-links for early entries are never starved
 * behind dozens of queued creates in a large batch.
 */
const LINK_SCORE_CHUNK_MAX_ENTRIES = 10;
const LINK_SCORE_CHUNK_MAX_BYTES = 700_000;

/** Conservative byte estimate for one scoring job — used only to decide chunk
 * boundaries (see LINK_SCORE_CHUNK_MAX_BYTES above), never sent anywhere itself.
 * `Buffer.byteLength(..., 'utf8')`, NOT `.length` (v2-review sweep, PR #392
 * unresolved thread, Codex, P2, posted twice/same finding): `.length` on a JS
 * string counts UTF-16 code units, undercounting multibyte (e.g. CJK) candidate
 * names relative to their real UTF-8 wire size — a chunk could exceed Convex's
 * sub-1MB argument ceiling despite passing this estimate, and the caught failure
 * silently dropped auto-links for the whole chunk. */
function estimateJobBytes(job: LinkScoreJob): number {
  return Buffer.byteLength(JSON.stringify(job), "utf8");
}

interface RelationTypeResult {
  type: string;
  reason: string;
}

function inferRelationType(
  _sourceCollection: string,
  _targetCollection: string,
  profile: CollectionProfile,
): RelationTypeResult {
  const type = profile.recommendedRelationTypes[0] ?? "related_to";
  const reason = `profile default (${type})`;
  return { type, reason };
}


// ── Tool Registration ───────────────────────────────────────────────────────

// BET-281: GOVERNED_COLLECTIONS replaced by runtime isGoverned() from collectionCache.
// The `governed` field on each collection doc is the SSOT.

const AUTO_LINK_CONFIDENCE_THRESHOLD = 35;
const MAX_AUTO_LINKS = 5;
const MAX_SUGGESTIONS = 5;

/** BR-111: entryId only for business-rules and standards; format BR-NNN or STD-NNN. All other collections use auto-generated IDs. */
const BR_STD_ENTRY_ID_REGEX = /^(BR|STD)-\d+$/;
const entryIdSchema = z.string().regex(BR_STD_ENTRY_ID_REGEX).optional().describe("Only for business-rules and standards. Must match BR-NNN or STD-NNN. Omit for all other collections — IDs are auto-generated.");

export const captureSchema = z.object({
  collection: z.string().optional().describe("Collection slug, e.g. 'tensions', 'business-rules', 'glossary', 'decisions'. Optional — classifier auto-routes when omitted."),
  name: z.string().describe("Display name — be specific (e.g. 'Convex adjacency list won't scale for graph traversal')"),
  description: z.string().describe("Full context — what's happening, why it matters, what you observed"),
  context: z.string().optional().describe("Optional additional context (e.g. 'Observed during context gather calls taking 700ms+')"),
  entryId: entryIdSchema,
  canonicalKey: z.string().optional().describe("Semantic type (e.g. 'decision', 'tension', 'vision'). Auto-assigned from collection if omitted."),
  data: z.record(z.unknown()).optional().describe("Explicit field values when you know the schema (e.g. canonical_key, cardinality_rule, required_fields). Merged with inferred values; user-provided wins."),
  links: z.array(z.object({
    to: z.string().describe("Target entry ID (e.g. '<PREFIX>-<n>')"),
    type: z.string().describe("Relation type (e.g. 'governs', 'related_to', 'informs')"),
  })).optional().describe("Relations to create after capture. Skips auto-link discovery when provided."),
  autoCommit: z.boolean().optional().describe("If true, commits the entry immediately after capture + linking. If omitted, Open mode workspaces auto-commit by default and consensus/role modes stay draft-first."),
  sourceRef: z.string().optional().describe("URI or path of the source document backing this entry (e.g. 'meeting-2026-03-28.md', 'import://batch-5'). Stored as top-level entry field, not in data."),
  sourceExcerpt: z.string().optional().describe("Verbatim excerpt from the source that backs this entry's claims. Stored as top-level entry field, not in data."),
  // WP-316 S3: Preview gate — dry-run mode. Returns what would happen, no DB writes.
  preview: z.boolean().optional().describe("If true, validates the capture without writing. Returns what would happen. Default false."),
  // WP-318 S2: Pre-write grounding — run link suggestion before creating entry.
  suggestOnly: z.boolean().optional().describe("If true, runs pre-write grounding (suggestLinksForCapture) and returns a groundingReport WITHOUT creating any entry. Use to preview graph participation before accepting. Default false."),
  // WP-318 S2: Format for groundingReport in response.
  format: z.enum(["agent", "human"]).optional().describe("Response format for grounding data. 'agent' (default): full JSON groundingReport. 'human': compressed summary string appended to the capture summary."),
  // WP-513: team+role to create AS OWNER of (rung 2 only); no-op pre-rung-2.
  ownerTeamEntryId: z.string().max(200).optional().describe("Owning team entry ID/ref (rung 2)."),
  ownerRoleEntryId: z.string().max(200).optional().describe("Owning role entry ID/ref (rung 2)."),
});

// WP-484 S2 (§3/§7 TEN-957): inline per-item relations — each batch entry can
// name relations to EXISTING Chain entries to create right after it is
// captured, resolving TEN-957 ("batch-capture lacks inline relations").
// Sibling-item (intra-batch) references are out of scope for this slice —
// `to` must be an existing entryId already on the Chain.
const batchCaptureRelationSchema = z.object({
  to: z.string().max(200).describe("Target entry ID already on the Chain, e.g. '<PREFIX>-<n>'."),
  type: z.string().max(200).describe("Relation type, e.g. 'related_to', 'informed_by', 'governs'."),
});

export const batchCaptureSchema = z.object({
  entries: z.array(z.object({
    // FEAT-160
    collection: z.string().max(200).optional().describe("Collection slug. Optional — auto-classified via LLM when omitted."),
    name: z.string().max(500).describe("Display name"),
    description: z.string().max(20000).describe("Full context / definition"),
    entryId: entryIdSchema,
    data: z.record(z.unknown()).optional().describe("Explicit field values (e.g. urgency, status, assignee). Merged with inferred values; user-provided wins."),
    canonicalKey: z.string().max(200).optional().describe("Semantic type (e.g. 'decision', 'tension', 'work_package'). Enables work-package redirect in createEntry when collection is 'chains'."),
    sourceRef: z.string().max(2000).optional().describe("URI or path of the source document backing this entry (e.g. 'meeting-2026-03-28.md', 'import://batch-5'). Stored as top-level entry field, not in data."),
    sourceExcerpt: z.string().max(5000).optional().describe("Verbatim excerpt from the source that backs this entry's claims. Stored as top-level entry field, not in data."),
    // TEN-957 (WP-484 S2): relations to create right after this entry is captured.
    relations: z.array(batchCaptureRelationSchema).max(10).optional().describe("Relations to create right after this entry is captured — each {to, type} links the NEW entry to an existing Chain entry."),
    // WP-513: team+role to create THIS entry AS OWNER of (rung 2 only).
    ownerTeamEntryId: z.string().max(200).optional().describe("Owning team entry ID/ref (rung 2)."),
    ownerRoleEntryId: z.string().max(200).optional().describe("Owning role entry ID/ref (rung 2)."),
  })).min(1).max(50).describe("Array of entries to capture"),
  autoCommit: z
    .boolean()
    .optional()
    .describe(
      "If true, commits created entries immediately after linking. " +
      "If omitted, Open mode workspaces commit by default and consensus/role modes stay draft-first."
    ),
  // WP-316 S3: Preview gate — dry-run mode. Returns what would happen, no DB writes.
  preview: z.boolean().optional().describe("If true, validates all captures without writing. Returns what would happen. Default false."),
});

export const captureClassifierSchema = z.object({
  enabled: z.boolean(),
  autoRouted: z.boolean(),
  agrees: z.boolean(),
  abstained: z.boolean(),
  topConfidence: z.number(),
  confidence: z.number(),
  reasons: z.array(z.string()),
  candidates: z.array(
    z.object({
      collection: z.string(),
      signalScore: z.number().optional(),
      confidence: z.number(),
      /** WP-316 S2: required-field cost — how many fields the agent must supply for this collection. */
      requiredFieldCount: z.number().optional(),
    }),
  ),
  agentProvidedCollection: z.string().optional(),
  overrideCommand: z.string().optional(),
  classifiedBy: z.enum(["llm", "heuristic", "explicit"]).optional(),
  confidenceTier: z.enum(["high", "medium", "low"]).optional(),
  reasoning: z.string().optional(),
});

export type CaptureClassifierMetadata = z.infer<typeof captureClassifierSchema>;

type CaptureToolResult = {
  content: Array<{ type: "text"; text: string }>;
  // Required (not optional) to mirror envelope.ts's ToolResult contract (DEC-1447): every
  // response this tool returns always sets structuredContent.
  structuredContent: z.infer<typeof captureOutputSchema> | EnvelopeError;
};

type CollectionResolution = {
  resolvedCollection?: string;
  classifierMeta?: CaptureClassifierMetadata;
  earlyResult?: CaptureToolResult;
};

function trackClassifierTelemetry(params: {
  workspaceId: string;
  predictedCollection: string;
  confidence: number;
  autoRouted: boolean;
  reasonCategory: ClassifierReasonCategory;
  explicitCollectionProvided: boolean;
  outcome: "auto-routed" | "fallback";
}): void {
  const telemetry = {
    predicted_collection: params.predictedCollection,
    confidence: params.confidence,
    auto_routed: params.autoRouted,
    reason_category: params.reasonCategory,
    explicit_collection_provided: params.explicitCollectionProvided,
  };

  trackCaptureClassifierEvaluated(params.workspaceId, telemetry);
  if (params.outcome === "auto-routed") {
    trackCaptureClassifierAutoRouted(params.workspaceId, telemetry);
    return;
  }
  trackCaptureClassifierFallback(params.workspaceId, telemetry);
}

function buildClassifierUnknownResult(): CaptureToolResult {
  return {
    content: [{
      type: "text" as const,
      text:
        "I could not infer a collection confidently from this input.\n\n" +
        "Please provide `collection`, or rewrite with clearer intent (decision/problem/definition/insight/work-package).",
    }],
    structuredContent: failure(
      "VALIDATION_ERROR",
      "Could not infer collection from input.",
      "Provide collection explicitly, or rewrite with clearer intent.",
      [{ tool: "collections", description: "List available collections", parameters: { action: "list" } }],
      { classifier: { enabled: true, autoRouted: false, agrees: false, abstained: true, topConfidence: 0, confidence: 0, reasons: [], candidates: [] } },
    ),
  };
}

function buildProvisionedCollectionSuggestions(
  candidates: Array<{ collection: string; signalScore?: number; confidence: number }>,
): string {
  return candidates.length
    ? candidates
      .map((c) => `- \`${c.collection}\` (${c.signalScore ?? c.confidence}% confidence)`)
      .join("\n")
    : "- No collection candidates were inferred confidently.";
}

function buildLowConfidenceResult(
  resolved: ResolvedCollection,
  classifierMeta: CaptureClassifierMetadata,
): CaptureToolResult {
  const allCandidates = [
    { collection: resolved.collection, confidence: resolved.confidence },
    ...resolved.alternatives,
  ];
  // WP-316 S2: enrich suggestions with requiredFieldCount from classifierMeta.candidates
  const candidateFieldCounts = new Map<string, number>();
  for (const c of classifierMeta.candidates) {
    if (c.requiredFieldCount !== undefined) {
      candidateFieldCounts.set(c.collection, c.requiredFieldCount);
    }
  }
  const suggestions = allCandidates
    .map((c) => {
      const fieldCost = candidateFieldCounts.get(c.collection);
      const fieldNote = fieldCost !== undefined ? `, ${fieldCost} required field${fieldCost === 1 ? "" : "s"}` : "";
      return `- \`${c.collection}\` (${c.confidence}% confidence${fieldNote})`;
    })
    .join("\n");
  const textBody =
    `Low confidence classification (${resolved.confidence}%, classified by ${resolved.classifiedBy}).\n\n` +
    `Predicted: \`${resolved.collection}\`\n` +
    `Reason: ${resolved.reasoning || "low signal"}\n\n` +
    "Choose one of these and retry with `collection`:\n" +
    `${suggestions}\n\n` +
    "Correction path: rerun with your chosen `collection`, or capture and use `entries action=move` later.";
  return {
    content: [{ type: "text" as const, text: textBody }],
    structuredContent: failure(
      "VALIDATION_ERROR",
      `Low confidence routing to '${resolved.collection}' (${resolved.confidence}%, ${resolved.classifiedBy}).`,
      "Rerun with explicit collection.",
      allCandidates.map((c) => ({
        tool: "capture",
        description: `Capture to ${c.collection}`,
        parameters: { collection: c.collection },
      })),
      { classifier: classifierMeta },
    ),
  };
}


function buildClassifierMeta(
  resolved: ResolvedCollection,
  overrides: Partial<CaptureClassifierMetadata> = {},
): CaptureClassifierMetadata {
  return {
    enabled: true,
    autoRouted: false,
    agrees: true,
    abstained: false,
    topConfidence: resolved.confidence,
    confidence: resolved.confidence,
    reasons: resolved.reasoning ? [resolved.reasoning] : [],
    candidates: [
      { collection: resolved.collection, confidence: resolved.confidence },
      ...resolved.alternatives.map((a) => ({ collection: a.collection, confidence: a.confidence })),
    ].slice(0, 3),
    classifiedBy: resolved.classifiedBy,
    confidenceTier: resolved.tier,
    reasoning: resolved.reasoning,
    ...overrides,
  };
}

/**
 * WP-316 S3 gap fix: enrich classifier candidates with requiredFieldCount from collection schema.
 * This gives agents a cost signal when choosing between low-confidence candidates.
 * Advisory — never throws, falls back to unmodified candidates.
 */
async function enrichCandidatesWithFieldCounts(meta: CaptureClassifierMetadata): Promise<void> {
  try {
    const cols = await getCollections();
    const reqBySlug = new Map(
      cols.map((c) => [c.slug, (c.fields ?? []).filter((f: { required?: boolean }) => f.required === true).length]),
    );
    meta.candidates = meta.candidates.map((c) => ({
      ...c,
      requiredFieldCount: reqBySlug.get(c.collection) ?? 0,
    }));
  } catch {
    // enrichment is advisory; never fail capture for this
  }
}

/**
 * BET-288 S0: Fire confusion-matrix telemetry on every classification.
 * Called from every code path that produces a ResolvedCollection.
 */
function emitClassificationTelemetry(
  resolved: ResolvedCollection,
  workspaceId: string,
  opts: { explicitCollectionProvided: boolean; autoRouted: boolean },
): void {
  const topAlt = resolved.alternatives?.[0] ?? null;
  trackCollectionClassified(workspaceId, {
    collection_slug: resolved.collection,
    thinking_layer: resolved.thinkingLayer ?? null,
    confidence: resolved.confidence,
    classified_by: resolved.classifiedBy,
    confidence_tier: resolved.tier,
    alternative_slug: topAlt?.collection ?? null,
    alternative_confidence: topAlt?.confidence ?? null,
    explicit_collection_provided: opts.explicitCollectionProvided,
    auto_routed: opts.autoRouted,
  });
}

async function resolveCaptureCollection(params: {
  collection?: string;
  name: string;
  description: string;
  workspaceId: string;
  explicitCollectionProvided: boolean;
}): Promise<CollectionResolution> {
  const {
    collection,
    name,
    description,
    workspaceId,
    explicitCollectionProvided,
  } = params;

  if (collection) {
    const resolved = await resolveCollection({ name, description });
    if (resolved) {
      const agrees = resolved.collection === collection;
      const classifierMeta = buildClassifierMeta(resolved, {
        autoRouted: false,
        agrees,
        agentProvidedCollection: collection,
        ...(!agrees && {
          overrideCommand: `capture collection="${resolved.collection}"`,
        }),
      });
      await enrichCandidatesWithFieldCounts(classifierMeta);
      // BET-288 S0: Fire confusion telemetry on every classification (explicit path)
      emitClassificationTelemetry(resolved, workspaceId, {
        explicitCollectionProvided: true,
        autoRouted: false,
      });
      if (!agrees) {
        trackClassifierTelemetry({
          workspaceId,
          predictedCollection: resolved.collection,
          confidence: resolved.confidence,
          autoRouted: false,
          reasonCategory: "low-confidence",
          explicitCollectionProvided: true,
          outcome: "fallback",
        });
        // WP-316 S2: track divergence — agent overrode classifier suggestion
        const agentInCandidates =
          resolved.alternatives.some((a) => a.collection === collection) ||
          resolved.collection === collection;
        trackClassifierDivergence(workspaceId, {
          classifier_collection: resolved.collection,
          agent_collection: collection,
          classifier_confidence: resolved.confidence,
          classifier_tier: resolved.tier,
          agent_in_candidates: agentInCandidates,
        });
      }
      return { resolvedCollection: collection, classifierMeta };
    }
    return {
      resolvedCollection: collection,
      classifierMeta: {
        enabled: true,
        autoRouted: false,
        agrees: false,
        abstained: true,
        topConfidence: 0,
        confidence: 0,
        reasons: [],
        candidates: [],
        agentProvidedCollection: collection,
      },
    };
  }

  const resolved = await resolveCollection({ name, description });
  if (!resolved) {
    trackClassifierTelemetry({
      workspaceId,
      predictedCollection: "unknown",
      confidence: 0,
      autoRouted: false,
      reasonCategory: "low-confidence",
      explicitCollectionProvided,
      outcome: "fallback",
    });
    return { earlyResult: buildClassifierUnknownResult() };
  }

  // FEAT-159: Tier-based routing replaces shouldAutoRouteClassification/isClassificationAmbiguous.
  // High+medium auto-route; low rejects and asks for explicit collection.
  const autoRoute = resolved.tier !== "low";
  const classifierMeta = buildClassifierMeta(resolved, { autoRouted: autoRoute });
  await enrichCandidatesWithFieldCounts(classifierMeta);

  // BET-288 S0: Fire confusion telemetry on every classification (classifier path)
  emitClassificationTelemetry(resolved, workspaceId, {
    explicitCollectionProvided,
    autoRouted: autoRoute,
  });

  if (!autoRoute) {
    trackClassifierTelemetry({
      workspaceId,
      predictedCollection: resolved.collection,
      confidence: resolved.confidence,
      autoRouted: false,
      reasonCategory: "low-confidence",
      explicitCollectionProvided,
      outcome: "fallback",
    });
    return {
      classifierMeta,
      earlyResult: buildLowConfidenceResult(resolved, classifierMeta),
    };
  }

  trackClassifierTelemetry({
    workspaceId,
    predictedCollection: resolved.collection,
    confidence: resolved.confidence,
    autoRouted: true,
    reasonCategory: "auto-routed",
    explicitCollectionProvided,
    outcome: "auto-routed",
  });

  return {
    resolvedCollection: resolved.collection,
    classifierMeta,
  };
}

const captureSuccessOutputSchema = z.object({
  entryId: z.string(),
  collection: z.string(),
  name: z.string(),
  // "draft_on_failure" is emitted when an auto-commit is REFUSED/failed (stays a draft) — the
  // same path that also emits coherencyRefusal; the strict schema must accept it or fail.
  status: z.enum(["draft", "committed", "proposed", "draft_on_failure"]),
  // WP-480 S1: `qualityScore` (required, client N/10) deleted — a named, accepted
  // breaking output-contract change. The server verdict stays as `qualityVerdict`.
  qualityVerdict: z.record(z.unknown()).optional(),
  classifier: captureClassifierSchema.optional(),
  studioUrl: z.string().optional(),
  warnings: z.array(z.string()).optional(),
  normalization: z.object({
    remapped: z.record(z.string()).optional(),
    rejected: z.array(z.string()).optional(),
  }).optional(),
  expectedFields: z.array(z.object({
    key: z.string(),
    type: z.string(),
    required: z.boolean().optional(),
  })).optional(),
  // TEN-2365: capture-time authority-domain proposal (PENDING ratification), when one was filed.
  authorityDomain: z.object({
    slug: z.string(),
    status: z.literal("proposal-pending"),
  }).optional(),
  // WP-465 surface parity: structured coherency refusal when an auto-commit is gate-refused.
  // Loose record — the CoherencyRefusal contract is authored in
  // convex/lib/gates/coherencyControls.ts; re-declaring it here would be a second SSOT.
  coherencyRefusal: z.record(z.unknown()).optional(),
  // WP-485 Slice 2b round 4 (Codex P2, FEAT-1370): the server's contradiction advisory is emitted
  // into structuredContent but was missing here — since this schema is `.strict()`, a real capture
  // response carrying it failed validation with `unrecognized_keys`. Loose record, same rationale as coherencyRefusal above.
  contradictionAdvisory: z.record(z.unknown()).optional(),
  fallbackApplied: z.boolean().optional(), // round 2 (PRRT_kwDORS4ils6cTNbF): classifier field above still names the original (pre-substitution) prediction
}).strict();

const captureClassifierOnlyOutputSchema = z.object({
  classifier: captureClassifierSchema,
}).strict();

// Fix 2 (WP-577 review round 3, TEN-2918 follow-up): captureSinglePreviewOutputSchema
// (lib/captureSinglePreviewOutput.ts) is the third union member — the `preview: true` branch's
// own honest shape, distinct from the real-create success shape and the classifier-only fallback.
export const captureOutputSchema = z.union([
  captureSuccessOutputSchema,
  captureSinglePreviewOutputSchema,
  captureClassifierOnlyOutputSchema,
]);

// STD-2 / DEC-1504: the batch output contract lives in ../lib/batchCaptureOutput.js.
// `batchCaptureOutputSchema` re-exported here because __tests__/tool-harness.test.ts imports it
// from this module; its two branch schemas have no importer of this module and stay there.
export {
  batchCaptureOutputSchema,
} from "../lib/batchCaptureOutput.js";

/**
 * TEN-194: Auto-commit only in Open mode. In consensus/role, draft-first always —
 * ignore autoCommit: true to prevent governance bypass.
 */
function shouldAutoCommitCapture(
  autoCommit: boolean | undefined,
  governanceMode: string | undefined,
): boolean {
  if (governanceMode !== "open") return false;
  return autoCommit === true || autoCommit === undefined;
}

/**
 * Build initial entry data from collection field definitions using type-aware defaults.
 * Single source of truth: FIELD_TYPE_DEFAULTS from fieldTypes.ts (BET-106, DEC-174).
 * Deduplicates logic previously inlined in both capture and batch-capture (TEN-305).
 */
function buildDataFromFields(
  fields: Array<{ key: string; type: string; [k: string]: unknown }>,
  descriptionField: string,
  descriptionValue: string,
): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.key === descriptionField) {
      data[field.key] = descriptionValue;
    } else {
      data[field.key] = FIELD_TYPE_DEFAULTS[field.type as FieldType] ?? null;
    }
  }
  return data;
}

// WP-484 S2 (§3): `capture` absorbs `batch-capture` → action=batch, `items[]`
// (each item may carry inline `relations` — TEN-957). Legacy `batch-capture`
// tool stays registered alongside (§7 "alongside" mechanic) — both call the
// SAME handleCapture/handleBatchCapture functions, one business-logic path.
const CAPTURE_ACTIONS = ["capture", "batch"] as const;
type CaptureAction = (typeof CAPTURE_ACTIONS)[number];

const captureItemSchema = batchCaptureSchema.shape.entries.element;

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of every absorbed action's
// params (single-capture fields relaxed to optional; batch adds `items`). `action` defaults to
// 'capture' — `capture`'s name is REUSED (not "alongside"-eligible) and its pre-S2 behavior took
// no `action` param, so a default preserves every existing bare single-entry call until DEC-283 S3.
export const captureCompoundSchema = z.object({
  action: z.enum(CAPTURE_ACTIONS).optional().default("capture").describe("'capture': create a single knowledge entry (the original capture behavior). 'batch': create multiple entries in one call via `items[]` (absorbs batch-capture; each item may carry inline `relations`)."),
  // For 'capture' — mirrors captureSchema, relaxed to optional (batch omits these).
  collection: z.string().max(200).optional().describe("For 'capture': collection slug, e.g. 'tensions', 'business-rules', 'glossary', 'decisions'. Optional — classifier auto-routes when omitted."),
  name: z.string().max(500).optional().describe("For 'capture': display name — required for this action."),
  description: z.string().max(20000).optional().describe("For 'capture': full context — required for this action."),
  context: z.string().max(5000).optional().describe("For 'capture': optional additional context."),
  entryId: entryIdSchema,
  canonicalKey: z.string().max(200).optional().describe("For 'capture': semantic type (e.g. 'decision', 'tension', 'vision'). Auto-assigned from collection if omitted."),
  data: z.record(z.unknown()).optional().describe("For 'capture': explicit field values. Merged with inferred values; user-provided wins."),
  links: z.array(z.object({
    to: z.string().max(200).describe("Target entry ID (e.g. '<PREFIX>-<n>')"),
    type: z.string().max(200).describe("Relation type (e.g. 'governs', 'related_to', 'informs')"),
  })).max(20).optional().describe("For 'capture': relations to create after capture. Skips auto-link discovery when provided."),
  autoCommit: z.boolean().optional().describe("For 'capture'/'batch': if true, commits immediately after capture + linking."),
  sourceRef: z.string().max(2000).optional().describe("For 'capture': URI or path of the source document backing this entry."),
  sourceExcerpt: z.string().max(5000).optional().describe("For 'capture': verbatim excerpt from the source backing this entry's claims."),
  preview: z.boolean().optional().describe("For 'capture'/'batch': if true, validates without writing."),
  suggestOnly: z.boolean().optional().describe("For 'capture': if true, runs pre-write grounding and returns a groundingReport WITHOUT creating any entry."),
  format: z.enum(["agent", "human"]).optional().describe("For 'capture': response format for grounding data."),
  // For 'batch' — mirrors batchCaptureSchema's `entries`, renamed `items` per §3.
  items: z.array(captureItemSchema).min(1).max(50).optional().describe("For 'batch': array of entries to capture, each may carry inline `relations`."),
  // WP-513: team+role to create AS OWNER of (rung 2 only).
  ownerTeamEntryId: z.string().max(200).optional().describe("For 'capture': owning team (rung-2)."),
  ownerRoleEntryId: z.string().max(200).optional().describe("For 'capture': owning role (rung-2)."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion, reusing the
// existing single-purpose schemas (Code Integrity — captureSchema/
// batchCaptureSchema already know these shapes).
const captureSingleVariant = captureSchema.extend({ action: z.literal("capture") });
const captureBatchVariant = z.object({
  action: z.literal("batch"),
  items: z.array(captureItemSchema).min(1).max(50),
  autoCommit: z.boolean().optional(),
  preview: z.boolean().optional(),
});

export const captureActionUnion = z.discriminatedUnion("action", [
  captureSingleVariant,
  captureBatchVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const CAPTURE_ACTION_SPECS: Record<CaptureAction, ActionParamSpec> = {
  capture: {
    params: ["collection", "name", "description", "context", "entryId", "canonicalKey", "data", "links", "autoCommit", "sourceRef", "sourceExcerpt", "preview", "suggestOnly", "format"],
    description: "name and description are required.",
  },
  batch: { params: ["items", "autoCommit", "preview"], description: "items (1-50, each may carry inline relations) is required." },
};

export function registerSmartCaptureTools(server: McpServer) {
  // §3b handler map — dispatched via dispatchDiscriminated.
  const captureHandlers: Record<CaptureAction, (data: any) => Promise<ToolResult>> = {
    capture: (data) => handleCapture(server, data),
    batch: (data) => handleBatchCapture(server, { entries: data.items, autoCommit: data.autoCommit, preview: data.preview }),
  };

  server.registerTool(
    "capture",
    {
      title: "Capture",
      description:
        "The tool for creating knowledge entries — single or batch. Two actions:\n\n" +
        "- **capture**: Create a single entry. Creates an entry, auto-links related entries, and returns a quality " +
        "scorecard — all in one call. Provide a name and description; `collection` is optional — the classifier " +
        "auto-routes when omitted.\n" +
        "- **batch**: Create multiple entries in one call via `items[]` (absorbs batch-capture). Each item may carry " +
        "inline `relations` created right after that entry is captured.\n\n" +
        "**action=capture details:**\n" +
        "Supported collections with smart profiles: tensions, business-rules, glossary, decisions, features, " +
        "audiences, strategy, standards, maps, chains, insights, assumptions, principles, tracking-events.\n" +
        "All other collections get an ENT-{random} ID and sensible defaults.\n\n" +
        "**Explicit data:** When you know the schema, pass `data: { field: value }` to set fields directly. " +
        "Top-level `name` and `description` always win for those fields. `data` wins over inference for all other fields.\n\n" +
        "**Compound capture:** Pass `links` to create relations in the same call (skips auto-link discovery). " +
        "In Open mode, entries commit by default when `autoCommit` is omitted. " +
        "Pass `autoCommit: true` to promote the entry from draft to SSOT immediately after linking when you want to be explicit. " +
        "Governed collections (glossary, business-rules, principles, standards, strategy, features, architecture) " +
        "will warn but still commit — use only when you're certain.\n\n" +
        "Entries are created as `draft` first, then may publish immediately depending on governance and `autoCommit`. Use `entries action=update` for post-creation adjustments.",
      inputSchema: captureCompoundSchema,
      annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(captureCompoundSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "capture", action }, () =>
        dispatchDiscriminated("capture", captureActionUnion, parsed.data, CAPTURE_ACTION_SPECS, captureHandlers),
      );
    }),
  );

  // WP-484 S3 (clean break, TEN-2157): the standalone `batch-capture` tool
  // registration retired here (registerBatchCaptureTool deleted below).
  // handleBatchCapture stays live via `capture action=batch`.
}

/**
 * WP-484 S2 (§3): extracted single-item capture handler — reused by the
 * legacy `capture` standalone tool AND the new `capture action=capture`
 * (unchanged shape; the noun itself absorbs `batch-capture` as `action=batch`,
 * §3 core-nouns table). One business-logic path.
 */
export async function handleCapture(
  server: McpServer,
  { collection, name, description, context, entryId, canonicalKey, data: userData, links, autoCommit, sourceRef, sourceExcerpt, preview, suggestOnly, format, ownerTeamEntryId, ownerRoleEntryId }: z.infer<typeof captureSchema>,
) {
      requireWriteAccess();

      const timingStart = Date.now();

      const wsCtx = await getWorkspaceContext();
      const explicitCollectionProvided = typeof collection === "string" && collection.trim().length > 0;

      const resolution = await resolveCaptureCollection({
        collection,
        name,
        description,
        workspaceId: wsCtx.workspaceId,
        explicitCollectionProvided,
      });
      if (resolution.earlyResult) {
        return resolution.earlyResult;
      }
      let resolvedCollection = resolution.resolvedCollection;
      const classifierMeta = resolution.classifierMeta;
      if (!resolvedCollection) {
        return buildClassifierUnknownResult();
      }

      // DEC-313 GAP 1: classifier auto-routed to 'chains' with 'work-packages' a candidate ->
      // inject canonicalKey='work_package' so createEntry's isBetSignal redirects. Classifier-driven only.
      let canonicalKeyAutoInjected = false;
      if (resolvedCollection === 'chains' && !canonicalKey && !explicitCollectionProvided
        && classifierMeta?.candidates?.some((c: { collection: string }) => c.collection === 'work-packages')) {
        canonicalKey = 'work_package';
        canonicalKeyAutoInjected = true;
      }

      const tAfterClassify = Date.now();

      // WP-691 S5 (TASK-127): no-match never offers Create Collection. Not explicit + no match
      // -> file into 'insights', else any system-stratum drawer. Explicit-but-missing, or no
      // core drawer at all -> refuse. Clears an AUTO-injected canonicalKey (never a caller's) on
      // fallback — 'work_package' routed for the ORIGINAL missing collection is stale once moved.
      let fallbackApplied = false; // round 2 (PRRT_kwDORS4ils6cTNbF): flags a fallback substitution
      let col = await kernelQuery<CollectionDoc | null>("chain.getCollection", { slug: resolvedCollection });
      if (!col && !explicitCollectionProvided) {
        const allCollections = await getCollections();
        const fallback = allCollections.find((c) => c.slug === "insights") ?? allCollections.find((c) => c.stratum === "system");
        // Round 2 (PRRT_kwDORS4ils6cTtvl): caller-supplied canonicalKey='work_package' names
        // 'chains' as its only valid collection (DEC-313) — refuse (mirrors BR-55) vs. mistag.
        if (fallback && canonicalKey === "work_package" && !canonicalKeyAutoInjected && fallback.slug !== "chains") {
          const msg = `\`work_package\` entries must be in the \`chains\` collection, not \`${fallback.slug}\`. \`${resolvedCollection}\` doesn't exist here, so there's no chains-equivalent drawer to fall back into.`;
          return { content: [{ type: "text" as const, text: msg }], structuredContent: failure("VALIDATION_FAILED", `canonicalKey 'work_package' requires 'chains'; fallback target '${fallback.slug}' doesn't qualify.`, msg) };
        }
        if (fallback) { resolvedCollection = fallback.slug; col = fallback; fallbackApplied = true; if (canonicalKeyAutoInjected) canonicalKey = undefined; }
      }
      if (!col) {
        const msg = `Collection \`${resolvedCollection}\` not found. Creating a collection is not self-serve — ` +
          `ask a platform admin to provision it, or enable the module ontology pack that owns it. Use \`collections action=list\` to see what exists.`;
        return {
          content: [{ type: "text" as const, text: msg }],
          structuredContent: failure(
            "NOT_FOUND", `Collection '${resolvedCollection}' not found.`, msg,
            [{ tool: "collections", description: "List collections", parameters: { action: "list" } }],
          ),
        };
      }

      // BR-111: entryId only allowed for business-rules and standards. Runs AFTER the fallback
      // above so a reserved BR-N/STD-N id can't ride a fallback swap into another drawer.
      if (entryId && resolvedCollection !== "business-rules" && resolvedCollection !== "standards") {
        return {
          content: [{
            type: "text" as const,
            // BR-111
            text: `# entryId Not Allowed\n\n\`entryId\` is only valid for \`business-rules\` and \`standards\` collections. For \`${resolvedCollection}\`, omit \`entryId\` — IDs are auto-generated.`,
          }],
          structuredContent: failure(
            "INVALID_ENTRY_ID",
            `entryId is only allowed for business-rules and standards. Omit for ${resolvedCollection}.`,
            "Remove entryId from your capture call.",
            [],
          ),
        };
      }

      const profile = await getProfile(resolvedCollection);

      // WP-318 S2 E1: Pre-write grounding — call suggestLinksForCapture in parallel with collection fetch.
      // Graceful degradation: grounding NEVER blocks capture. Any error → null.
      // Only runs when there's searchable content and auto-discovery is not skipped.
      const skipAutoDiscoveryEarly = links && links.length > 0;
      const groundingStart = Date.now();
      const groundingPromise = (!skipAutoDiscoveryEarly && (name || description))
        ? kernelQuery<GatewayRouteReturnByName<"chain.suggestLinksForCapture">>("chain.suggestLinksForCapture", {
            entries: [{ name, description: description ?? "", collectionHint: resolvedCollection }],
            limit: 5,
            threshold: 2,
          }).catch(() => null)
        : Promise.resolve(null);

      // WP-318 S2 E1: Await grounding result — capped at 500ms to preserve fail-open on latency.
      const groundingRaw = await Promise.race([
        groundingPromise,
        new Promise<null>((r) => setTimeout(() => r(null), 500)),
      ]);
      const groundingElapsedMs = Date.now() - groundingStart;
      const groundingEntry = (groundingRaw as any)?.[0] ?? null;
      const groundingRelated: Array<{
        entryId: string; name: string; collectionSlug: string;
        overlapRatio: number; recommendedRelationType: string; reasoning: string;
      }> = groundingEntry?.related ?? [];
      const groundingDuplicates: Array<{
        entryId: string; name: string; collectionSlug: string;
        matchType: "related" | "possible_duplicate"; overlapRatio: number;
      }> = groundingEntry?.duplicates ?? [];

      // WP-319 S2: Consume governance[] from query response (moved from local isGoverned loop).
      // governance[] is pre-computed by suggestLinksForCapture (WP-319 S1) — no async round trips needed.
      // Severity field from the query is ignored here; MCP banner display does not distinguish severity yet.
      const groundingGovernance: Array<{ entryId: string; name: string; collectionSlug: string }> =
        (groundingEntry?.governance ?? []).map((g: any) => ({
          entryId: g.entryId,
          name: g.name,
          collectionSlug: g.collectionSlug,
        }));

      const groundingReport = {
        related: groundingRelated.map((r) => ({
          entryId: r.entryId,
          name: r.name,
          collectionSlug: r.collectionSlug,
          overlapRatio: r.overlapRatio,
          recommendedRelationType: r.recommendedRelationType,
          reasoning: r.reasoning,
        })),
        duplicates: groundingDuplicates.map((d) => ({
          entryId: d.entryId,
          name: d.name,
          collectionSlug: d.collectionSlug,
          matchType: d.matchType,
          overlapRatio: d.overlapRatio,
        })),
        governance: groundingGovernance,
      };

      // WP-318 S2 E3: suggestOnly — return grounding preview without creating entry
      if (suggestOnly) {
        const hasSuggestions = groundingReport.related.length > 0 || groundingReport.duplicates.length > 0;
        const summaryText = hasSuggestions
          ? `Grounding preview for "${name}": ${groundingReport.related.length} related, ${groundingReport.duplicates.length} possible duplicates. No entry created.`
          : `Grounding preview for "${name}": no similar entries found. Safe to capture.`;
        return {
          content: [{
            type: "text" as const,
            text: `# Grounding Preview — No Entry Created\n\n${summaryText}\n\n` +
              (groundingReport.related.length > 0
                ? `## Related entries\n${groundingReport.related.map((r) => `- **${r.entryId}** ${r.name} [${r.collectionSlug}] — ${r.reasoning}`).join("\n")}\n\n`
                : "") +
              (groundingReport.duplicates.length > 0
                ? `## Possible duplicates\n${groundingReport.duplicates.map((d) => `- **${d.entryId}** ${d.name} [${d.collectionSlug}] (${d.matchType}, overlap: ${d.overlapRatio})`).join("\n")}\n\n`
                : "") +
              (groundingReport.governance.length > 0
                ? `## Governance entries to review\n${groundingReport.governance.map((g) => `- **${g.entryId}** ${g.name} [${g.collectionSlug}]`).join("\n")}\n\n`
                : "") +
              `_Call \`capture\` without \`suggestOnly\` to proceed with entry creation._`,
          }],
          structuredContent: {
            ...success(
              summaryText,
              { groundingReport, outcome: "suggest_only" as const },
              // Fix 3 sibling (TEN-2918 follow-up): thread the caller's full original payload —
              // see lib/captureForRealAction.ts's doc comment.
              buildCaptureForRealNextAction({
                collection: resolvedCollection, name, description, context, entryId, canonicalKey,
                data: userData, links, autoCommit, sourceRef, sourceExcerpt, ownerTeamEntryId, ownerRoleEntryId,
              }),
            ),
          },
        };
      }

      // 2. Build data with profile defaults + inference
      const data = buildDataFromFields(col.fields ?? [], profile.descriptionField, description);

      for (const def of profile.defaults) {
        if (def.value !== "infer" && def.value !== "today") {
          data[def.key] = def.value;
        }
      }

      if (profile.inferField) {
        const inferred = profile.inferField({
          collection: resolvedCollection, name, description, context, data, entryId: "",
          linksCreated: [], linksSuggested: [], collectionFields: col.fields ?? [],
        });
        for (const [key, val] of Object.entries(inferred)) {
          if (val !== undefined && val !== "") {
            data[key] = val;
          }
        }
      }

      if (isEmptyValue(data.date)) {
        const inferredSourceDate = inferSourceDate(name, description, context, sourceRef, sourceExcerpt);
        if (inferredSourceDate && (col.fields ?? []).some((field) => field.key === "date")) {
          data.date = inferredSourceDate;
        }
      }

      // Merge user-provided data (wins over inference). WP-574/TEN-2916: NO key is filtered — a
      // `key !== "name"` strip discarded explicitly supplied mirror values while the batch path
      // never filtered. The write gate owns mirror semantics (blank coalesces from entry.name).
      if (userData && typeof userData === "object") Object.assign(data, userData);

      // Top-level description always wins
      data[profile.descriptionField || "description"] = description;

      // BET-281/WP-640 S2 (TASK-63): Identify work_package captures via canonicalKey (primary) or defaultCanonicalKey — isBetCapture renamed isWorkPackageCapture (mechanical).
      const colMeta = await getCollectionBySlug(resolvedCollection);
      const isWorkPackageCapture = canonicalKey === "work_package" || colMeta?.defaultCanonicalKey === "work_package";

      // BET-125: decomposeContent (strategy goals/rationale/scope; bet problem/elements/doneWhen) + canonicalizeSelects
      let entryWarnings: string[] = [];
      // BET-288: derive strategy check from thinkingLayer instead of hardcoded slug
      const isStrategyCapture = colMeta?.thinkingLayer === "strategy";
      const shouldDecompose =
        (isStrategyCapture || isWorkPackageCapture) &&
        description.trim().length > 0;
      if (shouldDecompose) {
        try {
          const decomposed = await kernelCall<GatewayRouteReturnByName<"chain.decomposeContent">>(
            "chain.decomposeContent",
            {
              collectionSlug: resolvedCollection,
              entryName: name,
              description,
              existingData: data,
              ...(isWorkPackageCapture ? { canonicalKey: "work_package" } : {}),
              ...(colMeta?.thinkingLayer ? { thinkingLayer: colMeta.thinkingLayer } : {}),
            },
          );
          if (decomposed?.decomposed) {
            for (const [key, val] of Object.entries(decomposed.decomposed)) {
              if (isEmptyValue(data[key]) && isNonEmptyValue(val)) {
                data[key] = val;
              }
            }
          }
        } catch {
          // Non-blocking: capture proceeds without decomposition (BET-125 contract)
          entryWarnings.push("Content decomposition skipped (transient error). Entry saved with description only.");
        }
      }
      canonicalizeSelects(data, col.fields ?? [], isWorkPackageCapture);

      // BET-132: bet captures keep flat fields (problem, appetite, etc.) — no data.links wrapping

      // 3b. Status is now resolved server-side (DEC-896 / BR-76): Open mode → 'active', else 'draft'.
      // Do NOT pass explicit status here so governance mode governs. Callers that need draft can pass it.
      const agentId = getAgentSessionId();

      // 4. Create entry (Convex generates sequential ID when entryId not provided)
      let finalEntryId: string;
      let internalId: string;
      let normalizationMeta: { remapped: Record<string, string>; rejected: string[]; accepted: string[] } | undefined;
      // BET-272 S3: Formative quality hints — populated from createEntryWithClassification result (STD-101)
      let formativeHints: Array<{ field: string; hint: string; severity: "advisory" }> = [];
      // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
      let relationSuggestionsFromCreate: Array<{ targetEntryId: string; targetName: string; relationType: string; confidence: number; reasoning: string }> = [];
      // TEN-2365: capture-time authority-domain proposal echo (PENDING ratification), when filed.
      let authorityDomainFromCreate: { slug: string; status: "proposal-pending" } | undefined;
      // WP-475 E1: heuristic coaching verdict delivered in the createEntry response.
      // `undefined` = field absent (older deploy) → fall back to the explicit call below.
      let coachingFromCreate: CoachingResultSubset | null | undefined;
      // DEC-896 / BR-76 S3: server returned 'active' — skip post-write commitEntry call.
      let wasAutoCommittedServerSide = false;
      try {
        // WP-577 / TEN-2918: the server's declared return type for this route now honestly
        // includes its preview-mode branch (convex/agentKnowledge/entries.ts,
        // CreateEntryWithClassificationPreviewResult) — GatewayRouteReturnByName picks it up
        // directly, no client-local widening type needed anymore.
        const result = await kernelMutation<GatewayRouteReturnByName<"chain.createEntry">>("chain.createEntry", {
          collectionSlug: resolvedCollection,
          entryId: entryId ?? undefined,
          name,
          // DEC-896 / BR-76: omit status — server resolves from governanceMode (Open→active, else draft).
          data,
          canonicalKey,
          createdBy: agentId ? `agent:${agentId}` : "capture",
          sessionId: agentId ?? undefined,
          ...(sourceRef ? { sourceRef } : {}),
          ...(sourceExcerpt ? { sourceExcerpt } : {}),
          ...(preview ? { preview: true } : {}),
          // WP-513 review round 3 (P1): rung-2 owner — no-op pre-rung-2.
          ...(ownerTeamEntryId ? { ownerTeamEntryId } : {}),
          ...(ownerRoleEntryId ? { ownerRoleEntryId } : {}),
          // WP-475 E1 (Codex P2 — CLI parity): user links are known UPFRONT, so the
          // post-relations re-evaluation below owns the single LLM run — defer the
          // create-time LLM schedule (deterministic, mirrors `pb capture --link`).
          // Auto-discovered links are unknown here: zero-user-link captures do NOT
          // defer (a link-less capture must keep its LLM run); the supersession
          // guard bounds the auto-link double-run race when timing allows.
          // Fail-safe: if the relations batch then creates nothing, the deferred row
          // keeps its delivered heuristic verdict with llmStatus 'skipped' (same
          // accepted shape as the CLI dying between create and re-eval).
          ...(links && links.length > 0 ? { deferLLMSchedule: true } : {}),
        });

        // WP-316 S3: Preview gate — return dry-run result without recording session activity.
        if (result && isPreviewCreateEntryResult(result)) {
          const hasGrounding = groundingReport.related.length > 0 || groundingReport.duplicates.length > 0 || groundingReport.governance.length > 0;
          const groundingSummary = hasGrounding
            ? `\n\n**Grounding:** ${[
                groundingReport.duplicates.length > 0 && `${groundingReport.duplicates.length} possible duplicate${groundingReport.duplicates.length > 1 ? "s" : ""}`,
                groundingReport.related.length > 0 && `${groundingReport.related.length} related entr${groundingReport.related.length > 1 ? "ies" : "y"}`,
                groundingReport.governance.length > 0 && `${groundingReport.governance[0]?.entryId} may govern this`,
              ].filter(Boolean).join(", ")}.`
            : "";
          // Phase 2 (D1): when the server predicts the governance WHY hard-gate would
          // reject this capture, the preview must say "would be BLOCKED" — not "would
          // capture" — so the dry-run contract stays honest for the newly gated inputs.
          const wouldBlock = result.wouldBlock === true;
          const headline = wouldBlock
            ? `Preview: would be BLOCKED — "${name}" [${resolvedCollection}]`
            : `Preview: would capture "${name}" [${resolvedCollection}]`;
          const previewEnvelope = success(
            wouldBlock
              ? `Preview: would be BLOCKED — "${name}" — no DB writes`
              : `Preview: would capture "${name}" — no DB writes`,
            {
              entryId: result.entryId,
              name,
              // P0 round 4 (fix 1/3, root): see resolveEffectiveCollection's doc comment
              // (lib/createEntryPreviewResult.ts) for why this must be the server's EFFECTIVE
              // post-routing collection, never the caller's pre-routing `resolvedCollection`.
              collection: resolveEffectiveCollection(result, preview, resolvedCollection),
              outcome: wouldBlock ? ("blocked" as const) : ("preview" as const),
              ...(wouldBlock && result.blockingReason ? { blockingReason: result.blockingReason } : {}),
              warnings: result.warnings ?? [],
              groundingReport,
              // TEN-2458: thread task-alignment verdict when kernel computed it for this preview.
              ...(result.taskAlignment ? { taskAlignment: result.taskAlignment } : {}),
            },
            // Fix 3 (WP-577 review re-review, TEN-2918 follow-up): thread the caller's full
            // original payload so a follow-up "capture for real" creates the SAME entry the
            // preview described — see lib/captureForRealAction.ts's doc comment for the bug
            // this closes (a bare {collection,name,description} dropped `data`/`links`/etc.,
            // so a required WHY supplied via `data` silently vanished on retry).
            buildCaptureForRealNextAction({
              collection: resolvedCollection, name, description, context, entryId, canonicalKey,
              data: userData, links, autoCommit, sourceRef, sourceExcerpt, ownerTeamEntryId, ownerRoleEntryId,
            }),
          );
          if (result.contract) {
            previewEnvelope.contract = result.contract;
          }
          const taskAlignmentNote = result.taskAlignment
            ? `\n\n**Task alignment:** ${result.taskAlignment.wording}`
            : "";
          return {
            content: [{ type: "text" as const, text: `# ${headline}\n\nNo DB writes — call without \`preview:true\` to capture for real.${result.warnings?.length ? `\n\n**Warnings:** ${result.warnings.join("; ")}` : ""}${groundingSummary}${taskAlignmentNote}` }],
            structuredContent: previewEnvelope,
          };
        }
        // Past this point `result` is provably the real-create branch (the preview branch
        // returned above) — TS's control-flow narrowing doesn't propagate through the
        // `"preview" in result` guard above for this union shape, so re-assert explicitly
        // rather than relying on it (PR #395, Lane C S3b annotation pass; still true after
        // WP-577's server-type fix — the discriminant check is the same shape, just against
        // the now-honest generated union instead of the old client-local widening type).
        const created = result as Exclude<typeof result, { preview: true }>;
        internalId = created.docId;
        finalEntryId = created.entryId;
        entryWarnings.push(...(created.warnings ?? []));
        // Server declares `normalization: Record<string, unknown> | null` (looser than this
        // file's own consumption shape) — the real convention (remapped/rejected/accepted) is
        // documented at the construction sites (convex/agentKnowledge/entries.ts) but not
        // statically typed there either (PR #395, Lane C S3b annotation pass).
        normalizationMeta = created.normalization as
          | { remapped: Record<string, string>; rejected: string[]; accepted: string[] }
          | undefined;
        // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
        relationSuggestionsFromCreate = created.relationSuggestions ?? [];
        // BET-272 S3: Quality hints from createEntryWithClassification (STD-101: single source)
        formativeHints = created.qualityHints ?? [];
        // TEN-2365: capture-time authority-domain proposal echo.
        authorityDomainFromCreate = created.authorityDomain;
        // WP-475 E1: consume the inline coaching verdict (undefined = older deploy).
        coachingFromCreate = (created as typeof created & { coaching?: typeof coachingFromCreate }).coaching;
        resolveGapsForEntry(name, created.entryId);
        // DEC-896 / BR-76 S3: server already committed (Open mode auto-commit). Mark and skip commitEntry.
        if ((created as typeof created & { status?: string }).status === "active") {
          wasAutoCommittedServerSide = true;
        }
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : String(error);
        // WP-575: a budget abort is an UNKNOWN capture, not a failed one (see the module).
        if (isGatewayTimeout(error)) return buildCaptureOutcomeUnknownResult(error, name, msg);
        if (msg.includes("Duplicate") || msg.includes("already exists")) {
          return {
            content: [{
              type: "text" as const,
              text: `# Cannot Capture — Duplicate Detected\n\n${msg}\n\nUse \`entries action=get\` to inspect the existing entry, or \`entries action=update\` to modify it.`,
            }],
            structuredContent: failure(
              "DUPLICATE",
              msg,
              "Use entries action=get to inspect the existing entry.",
              [
                { tool: "entries", description: "Get existing entry", parameters: { action: "get", entryId: entryId ?? name } },
                { tool: "entries", description: "Update existing entry", parameters: { action: "update", entryId: entryId ?? "" } },
              ],
            ),
          };
        }
        throw error;
      }

      const tAfterCreate = Date.now();

      // WP-319 S2: Record grounding outcome for observability (STD-155).
      // Fire-and-forget — never block capture on observability failure.
      void kernelMutation<GatewayRouteReturnByName<"chain.recordGroundingOutcome">>("chain.recordGroundingOutcome", {
        surface: "mcp",
        promptShown: groundingReport.governance.length > 0,
        highestRatio: Math.max(
          ...groundingReport.duplicates.map((d: any) => d.overlapRatio),
          ...groundingReport.related.map((r: any) => r.overlapRatio),
          0,
        ),
        hadGovernance: groundingReport.governance.length > 0,
        grounding_ms: groundingElapsedMs,
      }).catch(() => undefined);

      // 6. Discover candidates + build batch relations array (BET-105 E4)
      const linksCreated: LinkResult[] = [];
      const linksSuggested: LinkSuggestion[] = [];
      const userLinkResults: Array<{ label: string; ok: boolean }> = [];
      // WP-474 E5: auto-links the server judge advised but did NOT write (untypeable) — surfaced, never silently dropped.
      const advisedLinks: Array<{ targetName: string; targetCollection: string; relationType: string }> = [];
      // BET-167: Conflict candidates for pre-commit advisory (populated from search results)
      let conflictCandidates: Array<{ entryId: string; name: string; collection: string }> = [];

      type PendingRelation = {
        fromEntryId: string; toEntryId: string; type: string;
        // WP-474 E4-REWIRE: provenance marker + carried confidence threaded structurally
        // (not just as a string in meta.linkReason) so createEntryRelations persists them.
        proposedBy?: string; confidence?: number;
        meta: { source: "auto" | "user"; targetName: string; targetCollection: string; linkReason?: string };
      };
      const pendingRelations: PendingRelation[] = [];

      const skipAutoDiscovery = links && links.length > 0;
      const searchQuery = extractSearchTerms(name, description);
      if (searchQuery && !skipAutoDiscovery) {
        // BET-281: Populate hub cache before scoring; uses collectionCache (no extra DB call).
        const hubSlugs = await populateHubCache();
        const [searchResults, allCollections] = await Promise.all([
          kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query: searchQuery }),
          getCollections(),
        ]);

        const collMap = new Map<string, string>();
        for (const c of allCollections) collMap.set(c._id, c.slug);

        // PR #392 round 2 (Codex, finding 5 — memory): project each search-result
        // candidate to the MINIMAL shape actually needed downstream immediately — never
        // retain the full raw doc (which can carry an arbitrarily large `data` blob)
        // past this line. `preview` is computed HERE (not deferred) so the one
        // downstream consumer that used to read `c.data` (linksSuggested's preview
        // text, extractPreview) still works off a small, already-bounded (<=80 char)
        // string instead of the full original `data` object.
        const candidatesFiltered = (searchResults ?? [])
          .filter((r) => r.entryId !== finalEntryId && r._id !== internalId)
          .map((r) => ({
            name: r.name as string,
            entryId: r.entryId as string | undefined,
            collectionId: r.collectionId as string,
            preview: extractPreview(r.data, 80),
          }));

        // Lane C S4: ONE batched chain.scoreLinkCandidates call for every filtered
        // candidate — never a per-candidate call inside this .map(). Single-entry
        // capture sends exactly one job (this flow scores one entry's candidates); the
        // response is one result array per job. Server returns results already sorted
        // by score descending (positional `index`, immune to an undefined entryId);
        // re-associate via candidatesFiltered[result.index].
        //
        // PR #392 round 2 (Codex, finding 1): the served scoring call can genuinely
        // fail (network, rate limit, transient gateway error) — the deleted pre-migration
        // in-process local scorer could never throw. Fail OPEN, matching that property:
        // on failure, treat this entry as having zero auto-link candidates rather than
        // letting the whole capture fail. The entry is already created by this point;
        // only the auto-link/suggestion ADVISORY features degrade.
        let scores: LinkScoreResult[] = [];
        if (candidatesFiltered.length > 0) {
          try {
            const scoreJobResults = await kernelQuery<GatewayRouteReturnByName<"chain.scoreLinkCandidates">>("chain.scoreLinkCandidates", {
              collMap: Object.fromEntries(collMap),
              hubSlugs: [...hubSlugs],
              jobs: [
                buildScoreLinkCandidatesJob(
                  candidatesFiltered.map((r) => ({ name: r.name, collectionId: r.collectionId })),
                  name,
                  description,
                  resolvedCollection,
                ),
              ],
            });
            scores = scoreJobResults[0] ?? [];
          } catch { /* scoring failed; entry still created, zero auto-links this round */ }
        }
        const candidates = scores.map((s) => {
          const r = candidatesFiltered[s.index];
          return {
            ...r,
            collSlug: collMap.get(r.collectionId) ?? "unknown",
            confidence: s.score,
            confidenceReason: s.reason,
          };
        });

        let autoCount = 0;
        for (const c of candidates) {
          if (autoCount >= MAX_AUTO_LINKS) break;
          if (c.confidence < AUTO_LINK_CONFIDENCE_THRESHOLD) break;
          if (!c.entryId || !finalEntryId) continue;

          const { type: relationType, reason: relationReason } = inferRelationType(resolvedCollection, c.collSlug, profile);
          pendingRelations.push({
            fromEntryId: finalEntryId, toEntryId: c.entryId, type: relationType,
            // WP-474 E4-REWIRE: auto-defaulted relation (no one chose the type) — mark it
            // as auto-link and carry the numeric confidence structurally.
            proposedBy: "auto-link", confidence: c.confidence,
            meta: { source: "auto", targetName: c.name, targetCollection: c.collSlug,
              linkReason: `confidence ${c.confidence} (${c.confidenceReason}) + ${relationReason}` },
          });
          autoCount++;
        }

        const autoTargetIds = new Set(pendingRelations.map(r => r.toEntryId));
        for (const c of candidates) {
          if (linksSuggested.length >= MAX_SUGGESTIONS) break;
          if (c.entryId !== undefined && autoTargetIds.has(c.entryId)) continue;
          if (c.confidence < 10) continue;

          // PR #392 round 2 (finding 5): `c.preview` was already computed at projection
          // time (right after search), off the full `data` — never retained here.
          const reason = c.confidence >= AUTO_LINK_CONFIDENCE_THRESHOLD
            ? "high relevance (already linked)"
            : `"${c.name.toLowerCase().split(/\s+/).filter((w: string) => `${name} ${description}`.toLowerCase().includes(w) && w.length > 3).slice(0, 2).join('", "')}" appears in content`;

          linksSuggested.push({ entryId: c.entryId, name: c.name, collection: c.collSlug, reason, preview: c.preview });
        }

        // BET-167: Collect conflict candidates from auto-link search results for pre-commit advisory.
        // Reuses the search already done — no extra query needed.
        conflictCandidates = candidates
          .filter((c) => c.entryId && c.entryId !== finalEntryId)
          .slice(0, 3)
          .map((c) => ({ entryId: c.entryId as string, name: c.name as string, collection: c.collSlug as string }));
      }

      // 6b. Add user-specified links to batch
      if (links && links.length > 0 && finalEntryId) {
        for (const link of links) {
          pendingRelations.push({
            fromEntryId: finalEntryId, toEntryId: link.to, type: link.type,
            // WP-474 E4-REWIRE: user-chosen type — mark provenance, NO confidence (human-chosen).
            proposedBy: "user",
            meta: { source: "user", targetName: link.to, targetCollection: "unknown" },
          });
        }
      }

      // 7. Parallel post-create: batch relations + cardinality + contradiction + coaching
      const resolvedCK = canonicalKey;

      const batchRelationsPromise = pendingRelations.length > 0
        ? kernelMutation<GatewayRouteReturnByName<"chain.createEntryRelations">>(
            "chain.createEntryRelations", {
              // WP-474 E4-REWIRE: carry provenance + confidence through to the server seam.
              relations: pendingRelations.slice(0, 25).map(r => ({
                fromEntryId: r.fromEntryId, toEntryId: r.toEntryId, type: r.type,
                ...(r.proposedBy !== undefined ? { proposedBy: r.proposedBy } : {}),
                ...(r.confidence !== undefined ? { confidence: r.confidence } : {}),
              })),
              sessionId: agentId ?? undefined,
            },
          ).catch((err) => {
            entryWarnings.push(`Auto-linking partially failed: ${err instanceof Error ? err.message : "unknown error"}`);
            return null;
          })
        : Promise.resolve(null);

      const cardinalityPromise = resolvedCK
        ? kernelQuery<GatewayRouteReturnByName<"chain.checkCardinalityWarning">>("chain.checkCardinalityWarning", { canonicalKey: resolvedCK }).catch(() => null)
        : Promise.resolve(null);

      const contradictionPromise = runContradictionCheck(name, description);

      // WP-475 E1: chain.createEntry now delivers the heuristic coaching verdict in its
      // response — consume it instead of re-running the evaluation (the second call
      // inserted a duplicate qualityVerdicts row and scheduled the background LLM judge
      // twice). The explicit kernelMutation survives ONLY as a fallback for older
      // deploys whose createEntry response lacks the coaching field (deploy skew).
      const coachingPromise = coachingFromCreate !== undefined
        ? Promise.resolve(coachingFromCreate)
        : kernelMutation<GatewayRouteReturnByName<"quality.evaluateHeuristicAndSchedule">>("quality.evaluateHeuristicAndSchedule", {
            entryId: finalEntryId, context: "capture",
          }).catch(() => null);

      const [batchResult, cardinalityCheck, contradictionWarnings, coachingAtCreate] = await Promise.all([
        batchRelationsPromise, cardinalityPromise, contradictionPromise, coachingPromise,
      ]);

      // WP-475 E1 (Codex P2 — CLI parity): the relations batch landed AFTER the
      // create-time verdict, so relation-dependent rubric criteria would render
      // falsely failed. When ANY relation was created (user links and/or
      // auto-discovered), re-evaluate and render THAT verdict — the re-eval also
      // schedules the LLM run that user-link captures deferred at create.
      // Fail-open: a re-eval failure keeps the create-time verdict.
      let verdictResult = coachingAtCreate;
      if ((batchResult?.created ?? 0) > 0) {
        const reEval = await kernelMutation<GatewayRouteReturnByName<"quality.evaluateHeuristicAndSchedule">>("quality.evaluateHeuristicAndSchedule", {
          entryId: finalEntryId, context: "capture",
        }).catch(() => null);
        if (reEval?.verdict) verdictResult = reEval;
      }

      const tAfterRelations = Date.now();

      // Map batch results back to linksCreated / userLinkResults
      if (batchResult) {
        for (let i = 0; i < pendingRelations.length; i++) {
          const pending = pendingRelations[i];
          const result = batchResult.results[i];
          if (!result) continue;

          if (pending.meta.source === "auto") {
            if (result.ok) {
              linksCreated.push({
                targetEntryId: pending.toEntryId, targetName: pending.meta.targetName,
                targetCollection: pending.meta.targetCollection, relationType: pending.type,
                linkReason: pending.meta.linkReason,
              });
            } else if (result.status === 'advised') {
              advisedLinks.push({
                targetName: pending.meta.targetName, targetCollection: pending.meta.targetCollection,
                relationType: pending.type,
              });
            }
          } else {
            if (result.ok) {
              userLinkResults.push({ label: `✓ ${pending.type} → ${pending.toEntryId}`, ok: true });
              linksCreated.push({
                targetEntryId: pending.toEntryId, targetName: pending.meta.targetName,
                targetCollection: pending.meta.targetCollection, relationType: pending.type,
              });
            } else {
              userLinkResults.push({ label: `✗ ${pending.type} → ${pending.toEntryId}: ${result.error ?? "failed"}`, ok: false });
            }
          }
        }
      }

      // WP-474 E5: surface auto-links the judge advised but did not write — never silently dropped.
      if (advisedLinks.length > 0) {
        entryWarnings.push(`${advisedLinks.length} auto-link(s) advised, not written (untypeable) — accept with \`relations action=create\` to promote.`);
      }

      // 8. WP-480 S1: the in-process client scorecard was deleted — quality is
      // judged ONCE server-side. The verdict rides `verdictResult` (rubric coaching
      // below) and the advisory `formativeHints`; the connector only renders them.
      const tAfterQuality = Date.now();

      const cardinalityWarning: string | null = cardinalityCheck?.warning ?? null;

      if (contradictionWarnings.length > 0) {
        await recordSessionActivity({ contradictionWarning: true });
      }

      let coachingSection = "";
      if (verdictResult?.verdict && verdictResult.verdict.tier !== "passive" && verdictResult.verdict.criteria.length > 0) {
        coachingSection = formatRubricCoaching(verdictResult);
      }

      // 11. Track quality verdict
      if (verdictResult?.verdict) {
        try {
          const wsForTracking = await getWorkspaceContext();
          const v = verdictResult.verdict;
          const failedCount = (v.criteria ?? []).filter((c: any) => !c.passed).length;
          trackQualityVerdict(wsForTracking.workspaceId, {
            entry_id: finalEntryId,
            entry_type: v.canonicalKey ?? resolvedCollection,
            tier: v.tier,
            context: "capture",
            passed: v.passed,
            source: verdictResult.source ?? "heuristic",
            criteria_total: v.criteria?.length ?? 0,
            criteria_failed: failedCount,
            llm_scheduled: v.tier !== "passive",
            methodology_version: v.methodologyVersion,
          });
        } catch { /* tracking is advisory */ }
      }

      // 12. Auto-commit if requested (or defaulted via Open governance mode).
      // BET-76 FEAT-111: When autoCommit is not explicitly set, default to true for
      // Open mode workspaces. Consensus/role modes retain draft-first behavior.
      // TEN-1810: Refresh governanceMode in case workspace flipped to Open after session start.
      const freshGovernanceMode = await refreshWorkspaceGovernanceMode();
      const shouldAutoCommit = shouldAutoCommitCapture(autoCommit, freshGovernanceMode);

      // BET-167 doneWhen #4: Conflict pre-check before auto-commit (advisory, never blocking).
      // When auto-link discovery was skipped (user provided explicit links), run a lightweight
      // search now so conflict candidates are populated before the commit.
      if (shouldAutoCommit && finalEntryId && conflictCandidates.length === 0) {
        try {
          const conflictResults = await kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query: name });
          conflictCandidates = (conflictResults ?? [])
            .filter((r: any) => r.entryId && r.entryId !== finalEntryId && r._id !== internalId)
            .slice(0, 3)
            .map((r: any) => ({ entryId: r.entryId as string, name: r.name as string, collection: r.collectionSlug ?? "unknown" }));
        } catch {
          // Advisory only — proceed without conflict check on failure
        }
      }

      let finalStatus: "draft" | "committed" | "proposed" | "draft_on_failure" = "draft";
      let commitError: string | null = null;
      // WP-465 surface parity: full refusal payload (routes + mode) for the coherency refused path.
      let commitRefusal: Record<string, unknown> | null = null;
      // WP-485 Slice 2b (R2, FEAT-1370): the server's typed advisory, when commitEntry's
      // contradiction gate found a high-confidence match (DEC-1321 — advisory only).
      let contradictionAdvisory: ContradictionAdvisory | null = null;
      // DEC-896 / BR-76 S3: server already committed in Open mode — skip commitEntry entirely.
      //
      // KNOWN GAP (WP-485 Slice 2b round 4, Codex P1, FEAT-1370 — DEFERRED, not silently
      // resolved, flagged as the MOST IMPACTFUL open item from this review loop). This branch
      // is the MAIN, common auto-commit path — chain.createEntry itself sets status:'active'
      // for the default Open-governance case (createEntryPreparation.ts's rung 5) — and it
      // never discovers or forwards conflicts, so the contradiction gate this whole slice wired
      // up is DEAD CODE for ordinary captures; only entries forced/clamped to draft (the `else`
      // branch below) exercise it. Not fixed here because the honest root-fix is materially
      // bigger than everything else in this round: `chain.createEntry` has no `conflicts` arg,
      // and adding one means wiring the contradiction evaluate() gate into
      // createEntryPreparation.ts's status-resolution ladder — a piece of logic with a
      // documented history of subtle defects (PRI-24/DEC-173, "Capture-Path Integrity Defect
      // A") across 5 already-audited rungs (deprecated / forceDraft / explicit status /
      // coherency-mode floor / governance default). Inserting a 6th rung there under review-fix
      // time pressure is a worse risk than shipping this gap named and tracked. Follow-up:
      // give `createEntry` its own `conflicts` arg + contradiction gate + `contradictionAdvisory`
      // on its success response (mirroring commitEntry's Slice 2a shape), and discover conflicts
      // BEFORE calling createEntry on this path (both single- and batch-capture) so they can be
      // forwarded.
      if (wasAutoCommittedServerSide) {
        finalStatus = "committed";
        await recordSessionActivity({ entryModified: internalId });
        trackChainEntryCommitted(wsCtx.workspaceId, {
          entry_id: finalEntryId,
          collection: resolvedCollection,
          commit_method: "auto",
          surface: "mcp_capture",
        });
      } else if (shouldAutoCommit && finalEntryId) {
        try {
          // WP-485 Slice 2b (R2, FEAT-1370): discover, never decide — the enforcement decision
          // (was: a local BLOCKING_CONTRADICTION_THRESHOLD comparison, thrown before commitEntry
          // was ever called) now happens ONE place, server-side, inside commitEntry's
          // evaluate()/assessContradiction gate. Discovered conflicts are handed to the server
          // as evidence; the server computes contradictionAdvisory (DEC-1321: advisory only).
          const semanticConflicts = await discoverSemanticConflicts(name, description, resolvedCollection);
          const commitResult = await kernelMutation<GatewayRouteReturnByName<"chain.commitEntry">>("chain.commitEntry", {
            entryId: finalEntryId,
            author: agentId ? `agent:${agentId}` : undefined,
            sessionId: agentId ?? undefined,
            ...(semanticConflicts.length > 0 ? { conflicts: semanticConflicts } : {}),
          });
          const refusalReason = coherencyRefusalReason(commitResult);
          if (refusalReason) {
            // WP-465 slice ⑤ (Option B): the entry stays a draft — never accepted/tracked.
            commitError = `coherency gate refused this accept — ${refusalReason}`;
            // WP-465 surface parity: thread full refusal payload so routes + mode reach the caller.
            commitRefusal = commitResultField<Record<string, unknown>>(commitResult, "refusal") ?? null;
            finalStatus = "draft_on_failure";
          } else {
            finalStatus = commitResultField<string>(commitResult, "status") === "proposal_created" ? "proposed" : "committed";
            contradictionAdvisory = commitResultField<ContradictionAdvisory>(commitResult, "contradictionAdvisory") ?? null;
            if (finalStatus === "committed") {
              await recordSessionActivity({ entryModified: internalId });
              trackChainEntryCommitted(wsCtx.workspaceId, {
                entry_id: finalEntryId,
                collection: resolvedCollection,
                commit_method: "auto",
                surface: "mcp_capture",
              });
            }
          }
        } catch (e: unknown) {
          // BR-76 S2 — DEC-888: Surface failures via log + payload + session TEN. Never silent.
          commitError = e instanceof Error ? e.message : "unknown error";
          finalStatus = "draft_on_failure";
          await recordCommitFailure({ entryId: finalEntryId, error: e, sessionId: agentId, server });
        }
      }

      // 13. Format response
      const lines: string[] = [
        `# Captured: ${finalEntryId || name}`,
        `**${name}** added to \`${resolvedCollection}\` as \`${finalStatus}\``,
        `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
      ];

      if (classifierMeta) {
        lines.push("");
        lines.push("## Classification");
        if (fallbackApplied) { // round 2 (PRRT_kwDORS4ils6cTNbF): classifierMeta still names the original prediction
          lines.push(`Filed into \`${resolvedCollection}\` — the classifier's original pick was unavailable; routed here as a fallback, not a classifier decision.`);
        } else if (classifierMeta.abstained) {
          lines.push(`No classifier coverage for \`${resolvedCollection}\` — entry accepted as-is.`);
        } else if (classifierMeta.autoRouted) {
          lines.push(`Auto-routed to \`${resolvedCollection}\` (${classifierMeta.topConfidence}% confidence).`);
          if (classifierMeta.reasons.length > 0) {
            lines.push(`Reason: ${classifierMeta.reasons.join("; ")}.`);
          }
          lines.push(`Override: rerun with explicit \`collection\` if wrong.`);
        } else if (classifierMeta.agrees) {
          lines.push(`Classifier confirms \`${resolvedCollection}\` (${classifierMeta.topConfidence}% confidence).`);
        } else {
          const suggested = classifierMeta.candidates[0]?.collection ?? "unknown";
          lines.push(`Classifier suggests \`${suggested}\` (${classifierMeta.topConfidence}% confidence) — review classification.`);
          if (classifierMeta.reasons.length > 0) {
            lines.push(`Reason: ${classifierMeta.reasons.join("; ")}.`);
          }
          if (classifierMeta.overrideCommand) {
            lines.push(`Override: \`${classifierMeta.overrideCommand}\``);
          }
        }
      }

      // DEC-70: Cortex link for chain entries (e.g. bets) — structuredContent studioUrl
      const appUrl = process.env.PRODUCTBRAIN_APP_URL ?? "https://work.productbrain.io";
      const studioUrl =
        resolvedCollection === "chains"
          ? `${appUrl.replace(/\/$/, "")}/w/${wsCtx.workspaceSlug}/legacy/${internalId}`
          : undefined;
      if (studioUrl) {
        lines.push("");
        lines.push(`**View in Cortex:** ${studioUrl}`);
      }

      if (linksCreated.length > 0) {
        lines.push("");
        lines.push(`## Auto-linked (${linksCreated.length})`);
        for (const link of linksCreated) {
          const reason = (link as any).linkReason ? ` — because ${(link as any).linkReason}` : "";
          lines.push(`- -> **${link.relationType}** ${link.targetEntryId}: ${link.targetName} [${link.targetCollection}]${reason}`);
        }
      }

      if (userLinkResults.length > 0) {
        lines.push("");
        lines.push("## Requested links");
        for (const r of userLinkResults) lines.push(`- ${r.label}`);
      }

      if (finalStatus === "committed") {
        const wasAutoCommitted = autoCommit === undefined && freshGovernanceMode === "open";
        lines.push("");
        lines.push(`## Accepted: ${finalEntryId}`);
        if (wasAutoCommitted) {
          lines.push(`**${name}** added to your knowledge base.`);
        } else {
          lines.push(`**${name}** promoted to SSOT on the Chain.`);
        }
        if (await isGoverned(resolvedCollection)) {
          lines.push(`_Note: \`${resolvedCollection}\` is a governed collection — ensure this entry has been reviewed._`);
        }
        // WP-485 Slice 2b (R2, FEAT-1370): a high-confidence contradiction never blocks
        // (DEC-1321) but must stay VISIBLE — never a silent success.
        if (contradictionAdvisory) {
          lines.push("");
          lines.push("## Contradiction advisory");
          lines.push(contradictionAdvisory.reason);
          for (const c of contradictionAdvisory.conflictDetails) {
            lines.push(`- \`${c.chainEntryId}\` ${c.chainEntryName}: ${c.explanation}`);
          }
        }
      } else if (finalStatus === "proposed") {
        lines.push("");
        lines.push(`## Proposal created: ${finalEntryId}`);
        lines.push(`**${name}** requires consent before it can be accepted, so a proposal was created instead of accepting directly.`);
      } else if (commitError) {
        lines.push("");
        lines.push("## Accept failed");
        if (commitRefusal) {
          // WP-465 surface parity: render routes + mode from the structured coherency refusal.
          // MINOR 4: demote to H3 so the embedded refusal sits correctly under the H2 "## Accept failed".
          const refusalText = coherencyRefusedResult("capture", finalEntryId, commitRefusal, { headingLevel: 3 }).content[0].text;
          lines.push(...refusalText.split("\n"));
          lines.push(`Entry saved as draft — use \`entries action=commit entryId="${finalEntryId}"\` to promote when ready.`);
        } else {
          lines.push(`Error: ${commitError}. Entry saved as draft — use \`entries action=commit entryId="${finalEntryId}"\` to promote when ready.`);
        }
      }

      // BET-167: Advisory conflict notice — shown after commit status, never blocking
      if (shouldAutoCommit && conflictCandidates.length > 0) {
        lines.push("");
        lines.push("## Potential Overlaps");
        lines.push(`Auto-accepted with advisory: ${conflictCandidates.length} similar ${conflictCandidates.length === 1 ? "entry" : "entries"} found.`);
        for (const c of conflictCandidates) {
          lines.push(`- \`${c.entryId}\`: ${c.name} [${c.collection}]`);
        }
        lines.push("Consider reviewing for duplicates.");
      }

      if (linksSuggested.length > 0) {
        lines.push("");
        lines.push("## Suggested links (review and use `relations action=create`)");
        for (let i = 0; i < linksSuggested.length; i++) {
          const s = linksSuggested[i];
          const preview = s.preview ? ` — ${s.preview}` : "";
          lines.push(`${i + 1}. **${s.entryId ?? "(no ID)"}**: ${s.name} [${s.collection}]${preview}`);
        }
      }

      // WP-318 S2 E4: Grounding report — format:human renders compact summary; format:agent exposes via structuredContent only
      if (format === "human") {
        const hasDuplicates = groundingReport.duplicates.length > 0;
        const hasRelated = groundingReport.related.length > 0;
        const hasGov = groundingReport.governance.length > 0;
        if (hasDuplicates || hasRelated || hasGov) {
          lines.push("");
          const parts: string[] = [];
          if (hasDuplicates) parts.push(`${groundingReport.duplicates.length} possible duplicate${groundingReport.duplicates.length > 1 ? "s" : ""}`);
          if (hasRelated) parts.push(`${groundingReport.related.length} related entr${groundingReport.related.length > 1 ? "ies" : "y"}`);
          if (hasGov) parts.push(`${groundingReport.governance[0]?.entryId} may govern this`);
          lines.push(`_Grounding: ${parts.join(", ")}. Review and link if relevant._`);
        }
      }

      // BET-289 S2: Field-level writing guidance — shown when collection fields have guidance
      const fieldGuidanceSection = formatFieldGuidance(col.fields ?? []);
      if (fieldGuidanceSection) {
        lines.push("");
        lines.push(fieldGuidanceSection);

        // BET-289 S5: Track guidance application for observability (STD-155)
        const guidedFieldCount = (col.fields ?? []).filter((f: { writingGuidance?: string }) => f.writingGuidance).length;
        trackFieldGuidanceApplied(wsCtx.workspaceId, {
          collection: resolvedCollection,
          guided_field_count: guidedFieldCount,
        });
      }

      // BET-272 S3: Formative quality hints — advisory, never blocking (BR-144)
      if (formativeHints.length > 0) {
        lines.push("");
        lines.push("## Quality Hints (advisory)");
        for (const hint of formativeHints) {
          lines.push(`- **${hint.field}:** ${hint.hint}`);
        }
        lines.push(`_These hints are advisory — your entry was captured. Use \`entries action=update entryId="${finalEntryId}"\` to address them._`);
      }

      // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
      if (relationSuggestionsFromCreate.length > 0) {
        lines.push("");
        lines.push("## Suggested Relations (advisory)");
        for (const rs of relationSuggestionsFromCreate) {
          lines.push(`- **${rs.relationType}** → ${rs.targetEntryId} "${rs.targetName}" (confidence: ${rs.confidence})`);
        }
        lines.push(`_Create with: \`relations action=create fromId="${finalEntryId}" toId="TARGET-ID" type="TYPE"\`_`);
      }

      // TEN-2365: capture-time authority-domain filing — PENDING proposal, never auto-ratified (PAT-251)
      if (authorityDomainFromCreate) {
        lines.push("");
        lines.push(`domain: ${authorityDomainFromCreate.slug} (proposal pending — ratify at session close)`);
      }

      // STD-155: Track capture intelligence observability
      if (formativeHints.length > 0) {
        trackCaptureQualityHints(wsCtx.workspaceId, {
          collection: resolvedCollection,
          hint_count: formativeHints.length,
          hint_fields: formativeHints.map((h) => h.field),
        });
      }
      if (relationSuggestionsFromCreate.length > 0) {
        trackCaptureRelationSuggestions(wsCtx.workspaceId, {
          collection: resolvedCollection,
          suggestion_count: relationSuggestionsFromCreate.length,
          relation_types: relationSuggestionsFromCreate.map((rs) => rs.relationType),
          avg_confidence: Math.round(
            relationSuggestionsFromCreate.reduce((sum, rs) => sum + rs.confidence, 0) /
              relationSuggestionsFromCreate.length,
          ),
        });
      }

      // Strategy-link warning for work-package/goal (ENT-ldomlr). Codex round 5 PR #742: restored the bet disjunct — prod/UpCurrent captures stay legacy-spelled (DEC-1575).
      const isWorkPackageOrBetCapture = isWorkPackageCapture || resolvedCK === "bet";
      const isBetOrGoal = isWorkPackageOrBetCapture || resolvedCK === "goal";
      const hasStrategyLink = linksCreated.some((l) => l.targetCollection === "strategy");
      if (isBetOrGoal && !hasStrategyLink) {
        lines.push("");
        lines.push(
          `**Strategy link:** This ${isWorkPackageOrBetCapture ? "work package" : "goal"} doesn't connect to any strategy entry. Consider linking before accepting. Use \`relations action=suggest entryId="${finalEntryId}"\` to find strategy entries to connect to.`
        );
        await recordSessionActivity({ strategyLinkWarnedForEntryId: internalId });
      }

      // Cardinality warning for singleton types (DEC-zcuhvb)
      if (cardinalityWarning) {
        lines.push("");
        lines.push(`**Cardinality warning:** ${cardinalityWarning}`);
      }

      // Contradiction warnings — exact format per spec
      if (contradictionWarnings.length > 0) {
        lines.push("");
        lines.push("⚠ Contradiction check: proposed entry matched existing governance entries:");
        for (const w of contradictionWarnings) {
          lines.push(`- ${w.name} (${w.collection}, ${w.entryId}) — has 'governs' relation to ${w.governsCount} entries`);
        }
        lines.push("Run `context action=gather` on these entries before accepting.");
      }

      // Validation warnings from createEntry (FEAT-188: MCP Envelope Warnings)
      if (entryWarnings.length > 0) {
        lines.push("");
        lines.push("## Validation Warnings");
        for (const w of entryWarnings) {
          lines.push(`- ${w}`);
        }
      }

      // Rubric coaching (assertive/nudge only)
      if (coachingSection) {
        lines.push("");
        lines.push(coachingSection);
      }

      // Next steps: adapt based on what was already done
      // WP-480 S1: "has quality gaps" is now sourced from the SERVER verdict +
      // advisory formative hints (the deleted client scorecard used to drive this).
      const hasQualityGaps =
        (verdictResult?.verdict != null && verdictResult.verdict.passed === false) ||
        formativeHints.length > 0;
      lines.push("");
      lines.push("## Next Steps");
      const eid = finalEntryId || "(check entry ID)";
      if (finalStatus === "committed") {
        lines.push(`1. **Connect it:** \`relations action=suggest entryId="${eid}"\` — discover additional links`);
        if (hasQualityGaps) {
          lines.push(`2. **Improve quality:** \`entries action=update entryId="${eid}"\` — fill missing fields`);
        }
      } else if (finalStatus === "proposed") {
        lines.push(`1. **Connect it:** \`relations action=suggest entryId="${eid}"\` — discover additional links to support the proposal`);
        if (hasQualityGaps) {
          lines.push(`2. **Improve quality:** \`entries action=update entryId="${eid}"\` — strengthen the entry before approval`);
        }
      } else {
        if (userLinkResults.length === 0) {
          lines.push(`1. **Connect it:** \`relations action=suggest entryId="${eid}"\` — discover what this should link to`);
        }
        lines.push(`${userLinkResults.length === 0 ? "2" : "1"}. **Accept it:** \`entries action=commit entryId="${eid}"\` — promote from draft to SSOT on the Chain`);
        if (hasQualityGaps) {
          lines.push(`${userLinkResults.length === 0 ? "3" : "2"}. **Improve quality:** \`entries action=update entryId="${eid}"\` — fill missing fields`);
        }
      }

      // Advisory: workspace readiness hints (never blocking)
      try {
        const readiness = await kernelQuery<GatewayRouteReturnByName<"chain.workspaceReadiness">>("chain.workspaceReadiness");
        if (readiness && readiness.gaps && readiness.gaps.length > 0) {
          const topGaps = readiness.gaps.slice(0, 2);
          lines.push("");
          lines.push(`## Workspace Readiness: ${readiness.score}%`);
          for (const gap of topGaps) {
            lines.push(`- _${gap.label}:_ ${gap.guidance}`);
          }
        }
      } catch {
        // Readiness check is advisory — capture works without it
      }

      // Context-sensitive next: adapt based on what was already done
      const next: NextAction[] = [];
      if (finalStatus === "committed") {
        next.push({ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId: finalEntryId } });
      } else if (finalStatus === "proposed") {
        next.push({ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId: finalEntryId } });
      } else {
        if (userLinkResults.length === 0) {
          next.push({ tool: "relations", description: "Discover links", parameters: { action: "suggest", entryId: finalEntryId } });
        }
        next.push({ tool: "entries", description: "Accept onto Chain", parameters: { action: "commit", entryId: finalEntryId } });
      }

      // WP-480 S1: quality wording comes from the server rubric verdict (single
      // scorer, DEC-1418), not a client N/10 recompute.
      const qualityPhrase = verdictQualityPhrase(verdictResult);
      const summary = finalStatus === "committed"
        ? `Captured and accepted ${finalEntryId} (${name}) onto ${resolvedCollection}. ${qualityPhrase}`
        : finalStatus === "proposed"
          ? `Captured ${finalEntryId} (${name}) in ${resolvedCollection} and created a proposal for acceptance. ${qualityPhrase}`
          : `Captured ${finalEntryId} (${name}) as draft in ${resolvedCollection}. ${qualityPhrase}`;

      const expectedFields = (col.fields ?? []).map((f: any) => ({
        key: f.key, type: f.type, ...(f.required && { required: true }),
      }));

      // WP-316 S1a: Build CaptureContract for the EnvelopeSuccess.contract field.
      // Agents can read required fields upfront without a separate round-trip.
      const captureContractForEnvelope = {
        requiredFields: (col.fields ?? []).filter((f) => f.required === true).map((f) => f.key),
        allFields: (col.fields ?? []).map((f) => ({
          key: f.key,
          required: f.required === true,
          type: f.type,
          ...(f.helpText ? { helpText: f.helpText } : {}),
        })),
        ...(col.captureExpectation ? { captureExpectation: col.captureExpectation } : {}),
      };

      const totalMs = Date.now() - timingStart;
      const timing = {
        classifyMs: tAfterClassify - timingStart,
        createMs: tAfterCreate - tAfterClassify,
        autoLinkMs: tAfterRelations - tAfterCreate,
        qualityMs: tAfterQuality - tAfterRelations,
        totalMs,
      };

      const toolResult = {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: {
          ...success(
            summary,
            {
              entryId: finalEntryId,
              collection: resolvedCollection,
              name,
              status: finalStatus,
              // WP-465 surface parity: thread full refusal (routes + mode) for coherency-refused auto-commits.
              ...(commitRefusal ? { coherencyRefusal: commitRefusal } : {}),
              // WP-485 Slice 2b round 3 (Codex P2, FEAT-1370): mirror the server's contradiction
              // advisory into the structured payload — it was rendered in the human text (the
              // "## Contradiction advisory" section above) but silently absent from
              // structuredContent, so a structured-only MCP consumer saw a committed capture
              // with no warning even though the text response carried one.
              ...(contradictionAdvisory ? { contradictionAdvisory } : {}),
              // WP-480 S1: the server verdict rides `qualityVerdict` (named breaking
              // output-contract change, pre-launch).
              qualityVerdict: verdictResult?.verdict
                ? { ...verdictResult.verdict, source: verdictResult.source ?? "heuristic" }
                : undefined,
              ...(classifierMeta && { classifier: classifierMeta }),
              ...(fallbackApplied && { fallbackApplied: true }),
              ...(studioUrl && { studioUrl }),
              ...(entryWarnings.length > 0 && { warnings: entryWarnings }),
              ...(normalizationMeta && (Object.keys(normalizationMeta.remapped).length > 0 || normalizationMeta.rejected.length > 0) && {
                normalization: {
                  remapped: normalizationMeta.remapped,
                  rejected: normalizationMeta.rejected,
                },
              }),
              expectedFields,
              // BET-272 S3: Advisory quality hints — always included (empty array when all criteria pass)
              qualityHints: formativeHints,
              // BET-272 S5: Advisory relation suggestions from graph (BR-144, STD-147)
              relationSuggestions: relationSuggestionsFromCreate,
              // TEN-2365: capture-time authority-domain proposal (PENDING ratification), when filed.
              ...(authorityDomainFromCreate && { authorityDomain: authorityDomainFromCreate }),
              // WP-318 S2: Pre-write grounding report — always included (empty arrays when no suggestions)
              groundingReport,
            },
            next,
          ),
          // WP-316 S1a: Populate contract field so agents know required fields upfront.
          contract: captureContractForEnvelope,
          _meta: { timing },
        },
      };

      return toolResult;
}

/**
 * WP-484 S2 (§3): extracted batch-capture handler — reused by the legacy
 * `batch-capture` standalone tool AND the new `capture action=batch`
 * (absorbed per §3 core-nouns table). One business-logic path. TEN-957:
 * each entry may carry inline `relations` created right after capture.
 */
export async function handleBatchCapture(
  server: McpServer,
  { entries, autoCommit, preview }: z.infer<typeof batchCaptureSchema>,
) {
      requireWriteAccess();

      const batchTimingStart = Date.now();

      const agentId = getAgentSessionId();
      const createdBy = agentId ? `agent:${agentId}` : "capture";
      const wsCtx = await getWorkspaceContext();
      // TEN-1810: Refresh governanceMode in case workspace flipped to Open after session start.
      const freshGovernanceModeForBatch = await refreshWorkspaceGovernanceMode();
      const autoCommitApplied = shouldAutoCommitCapture(autoCommit, freshGovernanceModeForBatch);

      // ── FEAT-160: Parallel classification for entries without explicit collection ──
      const needsClassification = entries
        .map((e, i) => ({ ...e, index: i }))
        .filter((e) => !e.collection);

      let classificationMap = new Map<number, ResolvedCollection | null>();
      if (needsClassification.length > 0) {
        await server.sendLoggingMessage({
          level: "info",
          data: `Classifying ${needsClassification.length}/${entries.length} entries via LLM...`,
          logger: "product-brain",
        });
        const classifications = await Promise.all(
          needsClassification.map((e) =>
            resolveCollection({ name: e.name, description: e.description }).catch(() => null),
          ),
        );
        for (let i = 0; i < needsClassification.length; i++) {
          classificationMap.set(needsClassification[i].index, classifications[i]);
        }
      }

      type BatchEntryResult = {
        // PR #392 round 2 (Codex, finding 2): the ORIGINAL input position, carried
        // explicitly on every result (success or failure) from the moment it's known.
        // NEVER derive an entry's input position via `results.indexOf(...)` later — the
        // chunked pipeline below pushes phase-1 failures and phase-2 (chunk-flush)
        // results in an order that does NOT match `entries`' original order (phase-1
        // failures land immediately, in loop order; phase-2 results land per-chunk,
        // after that chunk's scoring call resolves) — `results.indexOf` on that
        // reordered array silently reports the WRONG input index.
        entryIdx: number;
        name: string;
        collection: string;
        entryId: string;
        ok: boolean;
        autoLinks: number;
        // WP-474 E5: auto-links the judge advised but did NOT write (untypeable).
        advisedLinks: number;
        status: "draft" | "committed" | "proposed" | "draft_on_failure";
        classifiedBy?: "llm" | "heuristic" | "explicit";
        confidence?: number;
        confidenceTier?: "high" | "medium" | "low";
        error?: string;
        commitError?: string;
        // WP-465 surface parity: full coherency refusal payload for batch-path refused auto-commits.
        commitRefusal?: Record<string, unknown>;
        warnings?: string[];
        normalization?: { remapped: Record<string, string>; rejected: string[] };
        // BET-167: Number of similar entries found during conflict pre-check
        overlapCount?: number;
        // TEN-2365: capture-time authority-domain proposal (PENDING ratification), when filed.
        authorityDomain?: { slug: string; status: "proposal-pending" };
        // TEN-957 (WP-484 S2): inline relations created right after this entry.
        relationsCreated?: number;
        // Finding #14: inline relations that filed an agent proposal instead of writing.
        relationsProposed?: number;
        // WP-577 review follow-up (P2, TEN-2918): the preview branch's own would-block verdict
        // for THIS entry (createEntryPreview.ts's `wouldBlock`/`blockingReason`) — undefined on
        // the real-create path. Lets the batch-preview response separate a would-be-blocked
        // item from the honest `wouldCapture` cohort instead of reporting it as would-succeed.
        wouldBlock?: boolean;
        blockingReason?: string;
        // Fix 1 (WP-577 review re-review, TEN-2918 follow-up): true when this entry's ID was
        // auto-allocated (no caller-supplied `entryId`) rather than exact. Consumed only by the
        // preview projection (lib/batchCaptureOutput.ts) — see that module's WouldCaptureEntry
        // doc comment for why a batch preview cannot report a concrete auto-ID.
        entryIdAssignedAtCapture?: boolean;
        // WP-485 Slice 2b round 2 (Codex P1): the server's per-entry contradiction advisory
        // (DEC-1321 — advisory only) was reaching intermediate `results` but silently dropped
        // from the final `captured` projection with no text rendering. Declared on the type so
        // both the structured `captured` mapping and the text section below can read it.
        contradictionAdvisory?: ContradictionAdvisory;
        timeoutOutcome?: BatchTimeoutOutcome; }; // WP-575 review (P1, TEN-1126) — gateway timeout, see lib/batchTimeoutCohort.ts.
      const results: BatchEntryResult[] = [];
      // Fix 3 (WP-577 review round 3) + Fix 2/3 (P0 round 4, TEN-2918 follow-up): catches
      // batch-LOCAL duplicate collection+name AND entryId — see lib/batchPreviewDuplicates.ts.
      const previewDupTracker = createBatchPreviewDuplicateTracker();
      type SkippedEntry = {
        index: number;
        name: string;
        suggestedCollection?: string;
        confidence?: number;
        alternatives?: Array<{ collection: string; confidence: number }>;
      };
      const skippedLowConfidence: SkippedEntry[] = [];

      await server.sendLoggingMessage({
        level: "info",
        data: `Batch capturing ${entries.length} entries...`,
        logger: "product-brain",
      });

      // BET-281: Use collectionCache instead of direct listCollections call.
      const allCollections = await getCollections();
      const hubSlugs = await populateHubCache();
      const collCache = new Map<string, CollectionDoc>();
      for (const c of allCollections) collCache.set(c.slug, c);
      const collIdToSlug = new Map<string, string>();
      for (const c of allCollections) collIdToSlug.set(c._id, c.slug);

      // Lane C S4 + PR #392 rounds 1-2: each entry that reaches search defers its
      // score-dependent work (auto-links, TEN-957 relations, commit, results.push) into
      // `finish`, invoked once its CHUNK (not the whole batch — round 2) has been
      // scored in one chain.scoreLinkCandidates call. `entryIdx`/`entry`/`resolvedSlug`/
      // `classifiedBy`/`confidence`/`confidenceTier` are carried alongside `finish` (not
      // just captured inside its closure) so a chunk-flush catch can build the same
      // failure result shape the original single-phase try/catch used.
      type PendingFinish = {
        // PR #392 round 2, finding 2 — see BatchEntryResult.entryIdx's comment.
        entryIdx: number;
        entry: (typeof entries)[number];
        // By construction, every entry reaching pendingFinish.push() below already
        // passed the `if (!col) { ...; continue; }` collection-resolution guard, so
        // resolvedSlug is always a defined string here (narrowed non-optional
        // deliberately — matches the string the original single-phase catch's
        // `collection: resolvedSlug` field already relied on via control-flow narrowing).
        resolvedSlug: string;
        classifiedBy: "llm" | "heuristic" | "explicit" | undefined;
        confidence: number | undefined;
        confidenceTier: "high" | "medium" | "low" | undefined;
        job: LinkScoreJob | null;
        finish: (scores: LinkScoreResult[]) => Promise<void>;
      };

      /**
       * PR #392 round 2 (Codex, findings 3+4 — chunked pipeline): scores and finishes
       * exactly ONE chunk. Called synchronously (awaited) from inside the main entries
       * loop below, so the NEXT chunk's entries never start their `createEntry` until
       * THIS chunk's relation writes (`finish`) have completed — restores write
       * interleaving (a large batch no longer queues 100 creates before the first
       * relation write) while keeping scoring calls bounded (~5 per 50-entry batch at
       * the default chunk size, vs. 50 before round 1's fix and 1 unbounded-payload call
       * before this round's chunking).
       */
      async function flushPendingChunk(chunk: PendingFinish[]): Promise<void> {
        if (chunk.length === 0) return;
        const jobsToScore = chunk.filter((p) => p.job !== null).map((p) => p.job as LinkScoreJob);
        let scoreResultsByJob: LinkScoreResult[][] = [];
        if (jobsToScore.length > 0) {
          try {
            scoreResultsByJob = await kernelQuery<GatewayRouteReturnByName<"chain.scoreLinkCandidates">>("chain.scoreLinkCandidates", {
              collMap: Object.fromEntries(collIdToSlug),
              hubSlugs: [...hubSlugs],
              jobs: jobsToScore,
            });
          } catch {
            // Whole-CHUNK scoring call failed — every entry in this chunk was already
            // created; none of them get auto-links this round (same "entry still
            // created" fail-open semantics the original per-entry try/catch had).
            scoreResultsByJob = jobsToScore.map(() => []);
          }
        }
        let scoreCursor = 0;
        for (const pending of chunk) {
          const scores = pending.job !== null ? (scoreResultsByJob[scoreCursor++] ?? []) : [];
          try {
            await pending.finish(scores);
          } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : String(error);
            results.push(buildBatchEntryFailure({
              entryIdx: pending.entryIdx, name: pending.entry.name, collection: pending.resolvedSlug,
              classifiedBy: pending.classifiedBy, confidence: pending.confidence, confidenceTier: pending.confidenceTier,
              error: msg,
            }));
          }
        }
      }

      let pendingFinish: PendingFinish[] = [];
      let chunkBytes = 0;

      for (let entryIdx = 0; entryIdx < entries.length; entryIdx++) {
        const entry = entries[entryIdx];

        if (entryIdx > 0 && entryIdx % 5 === 0) {
          await server.sendLoggingMessage({
            level: "info",
            data: `Captured ${entryIdx}/${entries.length} entries...`,
            logger: "product-brain",
          });
        }

        // ── Resolve collection: explicit or classified ──
        let resolvedSlug: string | undefined = entry.collection;
        let classifiedBy: "llm" | "heuristic" | "explicit" | undefined;
        let confidence: number | undefined;
        let confidenceTier: "high" | "medium" | "low" | undefined;

        if (resolvedSlug) {
          classifiedBy = "explicit";
        } else {
          const resolved = classificationMap.get(entryIdx);
          // BET-288 S0: Fire confusion telemetry for batch classifications
          if (resolved) {
            emitClassificationTelemetry(resolved, wsCtx.workspaceId, {
              explicitCollectionProvided: false,
              autoRouted: resolved.tier !== "low",
            });
          }
          if (!resolved || resolved.tier === "low") {
            skippedLowConfidence.push({
              index: entryIdx,
              name: entry.name,
              suggestedCollection: resolved?.collection,
              confidence: resolved?.confidence,
              alternatives: resolved?.alternatives.slice(0, 3),
            });
            continue;
          }
          resolvedSlug = resolved.collection;
          classifiedBy = resolved.classifiedBy;
          confidence = resolved.confidence;
          confidenceTier = resolved.tier;
        }

        const profile = await getProfile(resolvedSlug);
        const col = collCache.get(resolvedSlug);

        if (!col) {
          results.push(buildBatchEntryFailure({
            entryIdx, name: entry.name, collection: resolvedSlug, classifiedBy, confidence, confidenceTier,
            error: `Collection "${resolvedSlug}" not found`,
          }));
          continue;
        }

        // BR-111: entryId only allowed for business-rules and standards
        if (entry.entryId && resolvedSlug !== "business-rules" && resolvedSlug !== "standards") {
          results.push(buildBatchEntryFailure({
            entryIdx, name: entry.name, collection: resolvedSlug, classifiedBy, confidence, confidenceTier,
            // BR-111
            error: `entryId only allowed for business-rules and standards. Omit for ${resolvedSlug}.`,
          }));
          continue;
        }

        const data = buildDataFromFields(col.fields ?? [], profile.descriptionField, entry.description);

        for (const def of profile.defaults) {
          if (def.value !== "infer" && def.value !== "today") data[def.key] = def.value;
        }

        if (profile.inferField) {
          const inferred = profile.inferField({
            collection: resolvedSlug, name: entry.name, description: entry.description,
            data, entryId: "", linksCreated: [], linksSuggested: [], collectionFields: col.fields ?? [],
          });
          for (const [key, val] of Object.entries(inferred)) {
            if (val !== undefined && val !== "") data[key] = val;
          }
        }

        if (isEmptyValue(data.date)) {
          const inferredSourceDate = inferSourceDate(entry.name, entry.description);
          if (inferredSourceDate && (col.fields ?? []).some((field) => field.key === "date")) {
            data.date = inferredSourceDate;
          }
        }

        if (!data[profile.descriptionField] && !data.description && !data.canonical) {
          data[profile.descriptionField || "description"] = entry.description;
        }

        if (entry.data && typeof entry.data === "object") {
          for (const [k, v] of Object.entries(entry.data)) {
            if (v !== undefined && v !== null && v !== "") data[k] = v;
          }
        }

        // DEC-231: work-packages collection is canonical — collection slug is the sole discriminator
        // DEC-313 GAP 2: also detect work package via canonicalKey from batch input
        const batchIsBet = resolvedSlug === "work-packages" || entry.canonicalKey === "work_package";
        // BET-288: derive strategy check from thinkingLayer instead of hardcoded slug
        const batchIsStrategy = col?.thinkingLayer === "strategy";
        const shouldDecomposeBatch =
          (batchIsStrategy || batchIsBet) && entry.description?.trim();
        let batchDecomposeWarning: string | undefined;
        if (shouldDecomposeBatch) {
          try {
            const decomposed = await kernelCall<GatewayRouteReturnByName<"chain.decomposeContent">>(
              "chain.decomposeContent",
              {
                collectionSlug: resolvedSlug,
                entryName: entry.name,
                description: entry.description,
                existingData: data,
                ...(batchIsBet ? { canonicalKey: "work_package" } : {}),
                ...(col?.thinkingLayer ? { thinkingLayer: col.thinkingLayer } : {}),
              },
            );
            if (decomposed?.decomposed) {
              for (const [key, val] of Object.entries(decomposed.decomposed)) {
                if (isEmptyValue(data[key]) && isNonEmptyValue(val)) {
                  data[key] = val;
                }
              }
            }
          } catch {
            batchDecomposeWarning = "Content decomposition skipped (transient error). Entry saved with description only.";
          }
        }
        canonicalizeSelects(data, col.fields ?? [], batchIsBet);

        // BET-132: bet captures keep flat fields — no data.links wrapping

        // Fix 2/3 (P0 round 4, TEN-2918 follow-up): batch-local entryId + name duplicate checks.
        const previewDuplicate = checkBatchPreviewDuplicate(previewDupTracker, {
          preview, entryIdx, name: entry.name, collection: resolvedSlug,
          callerEntryId: entry.entryId, classifiedBy, confidence, confidenceTier,
        });
        if (previewDuplicate) { results.push(previewDuplicate); continue; }

        try {
          const rawResult = await kernelMutation<GatewayRouteReturnByName<"chain.createEntry">>("chain.createEntry", {
            collectionSlug: resolvedSlug,
            entryId: entry.entryId ?? undefined,
            name: entry.name,
            // DEC-896 / BR-76: omit status — server resolves from governanceMode (Open→active, else draft).
            data,
            createdBy,
            sessionId: agentId ?? undefined,
            canonicalKey: entry.canonicalKey ?? undefined,
            // BR-76 S2: Distinguish batch provenance in retrospective metrics (path a).
            originDetailOverride: "mcp-batch-capture",
            ...(entry.sourceRef ? { sourceRef: entry.sourceRef } : {}),
            ...(entry.sourceExcerpt ? { sourceExcerpt: entry.sourceExcerpt } : {}),
            ...(preview ? { preview: true } : {}),
            // WP-513 review round 3 (P1): rung-2 owner — no-op pre-rung-2.
            ...(entry.ownerTeamEntryId ? { ownerTeamEntryId: entry.ownerTeamEntryId } : {}),
            ...(entry.ownerRoleEntryId ? { ownerRoleEntryId: entry.ownerRoleEntryId } : {}),
          });

          // WP-577 / TEN-2918: `preview` now genuinely reaches this route — before this fix
          // every batch `preview: true` call threw ArgumentValidationError before this point,
          // so the code below always ran against a real create. It was ALREADY written to
          // tolerate a preview create for the parts that matter (inline relations forward
          // `preview` themselves ~2 screens down, and `batchWasAutoCommittedServerSide` reads
          // `result.status`, which is simply absent — hence `false` — on a preview result,
          // so the auto-commit block's condition already excluded it structurally EXCEPT for
          // one gap: `autoCommitApplied` on its own doesn't know about `preview`, so it would
          // fire a REAL `chain.commitEntry` against an entry that was never actually created —
          // fixed below at the `!preview` guard, ~2 screens down (Codex-shape bug this fix
          // would otherwise have introduced, since preview batch capture always errored before
          // reaching here). `result.docId`/`result.status` are read as if always present below
          // (pre-existing, unchanged) — safe because `docId` is only consumed inside the
          // `batchWasAutoCommittedServerSide` guard (false for preview) and `status` itself is
          // simply undefined (not a crash) on this route's preview branch.
          //
          // WP-577 review follow-up (P2, TEN-2918): read the preview union's wouldBlock/
          // blockingReason BEFORE the cast below discards that member — see
          // extractPreviewWouldBlock in lib/createEntryPreviewResult.ts for the full reasoning.
          const previewWouldBlock = extractPreviewWouldBlock(rawResult, preview);
          // P0 round 4 (fix 1/3, root): see resolveEffectiveCollection's doc comment
          // (lib/createEntryPreviewResult.ts) — reported in this entry's own result below, and
          // fed to reconcileBatchPreviewDuplicate (fix 2/3, lib/batchPreviewDuplicates.ts).
          const effectiveCollection = resolveEffectiveCollection(rawResult, preview, resolvedSlug);
          const result = rawResult as Exclude<typeof rawResult, { preview: true }>;

          const postRoutingDuplicate = reconcileBatchPreviewDuplicate(previewDupTracker, {
            wouldCapture: Boolean(preview && previewWouldBlock && !previewWouldBlock.wouldBlock),
            effectiveCollection, entryIdx, name: entry.name, callerEntryId: entry.entryId,
            classifiedBy, confidence, confidenceTier,
          });
          if (postRoutingDuplicate) { results.push(postRoutingDuplicate); continue; }

          const internalId = result.docId;
          const finalEntryId = result.entryId;
          const batchEntryWarnings = [
            ...(batchDecomposeWarning ? [batchDecomposeWarning] : []),
            ...(result.warnings ?? []),
          ];
          // WP-577 / TEN-2918 follow-up (P1): a preview never wrote `result.entryId` — resolving
          // gaps against it would mark a real knowledge gap resolved via a `gaps.resolve`
          // mutation against an entryId that doesn't exist in the DB. Matches the `!preview`
          // guard on the auto-commit block below.
          if (!preview) resolveGapsForEntry(entry.name, result.entryId);
          // DEC-896 / BR-76 S3: server already committed (Open mode auto-commit). Skip commitEntry.
          // KNOWN GAP (WP-485 Slice 2b round 4, Codex P1, FEAT-1370 — DEFERRED): same gap as the
          // single-capture path above (~line 1813) — this is the main auto-commit case and it
          // never discovers/forwards conflicts. See that comment for the full reasoning.
          const batchWasAutoCommittedServerSide = result.status === "active";
          let finalStatus: "draft" | "committed" | "proposed" | "draft_on_failure" = batchWasAutoCommittedServerSide ? "committed" : "draft";
          let commitError: string | undefined;
          // WP-465 surface parity: full refusal payload for coherency-refused batch auto-commits.
          let commitRefusal: Record<string, unknown> | undefined;
          // WP-485 Slice 2b (R2, FEAT-1370): the server's typed advisory, when commitEntry's
          // contradiction gate found a high-confidence match (DEC-1321 — advisory only).
          let contradictionAdvisory: ContradictionAdvisory | undefined;
          if (batchWasAutoCommittedServerSide) {
            await recordSessionActivity({ entryModified: internalId });
            trackChainEntryCommitted(wsCtx.workspaceId, {
              entry_id: finalEntryId,
              collection: resolvedSlug ?? undefined,
              commit_method: "auto",
              surface: "mcp_capture",
            });
          }

          // Lane C S4 + PR #392 rounds 1-2: search stays PER-ENTRY (it needs
          // finalEntryId, only known here) but SCORING is deferred — this entry's job
          // (if it has candidates) is collected into `pendingFinish` below and scored
          // once its CHUNK is flushed (see flushPendingChunk above), never one call per
          // entry.
          //
          // PR #392 round 2 (finding 5 — memory): project each search-result candidate
          // to the MINIMAL shape needed downstream IMMEDIATELY — the batch flow never
          // consumes a candidate's `data` (no preview text built here, unlike the
          // single-capture flow), so the raw search-result docs (which can carry large
          // `data` blobs) are never retained past this block, not even across the
          // deferred `finish` closure below.
          let candidatesFiltered: Array<{ name: string; entryId?: string; collectionId: string }> = [];
          let job: LinkScoreJob | null = null;
          const searchQuery = extractSearchTerms(entry.name, entry.description);
          if (searchQuery) {
            try {
              const searchResults = await kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query: searchQuery });
              candidatesFiltered = (searchResults ?? [])
                .filter((r) => r.entryId !== finalEntryId)
                .map((r) => ({
                  name: r.name as string,
                  entryId: r.entryId as string | undefined,
                  collectionId: r.collectionId as string,
                }));
              if (candidatesFiltered.length > 0) {
                job = buildScoreLinkCandidatesJob(
                  candidatesFiltered.map((r) => ({ name: r.name, collectionId: r.collectionId })),
                  entry.name,
                  entry.description,
                  resolvedSlug!,
                );
              }
            } catch { /* search failed; entry still created, candidatesFiltered stays empty */ }
          }

          // PR #392 round 3 (Codex P2): check BEFORE appending this entry to the
          // current chunk, not after. Checking after (the round-2 shape) let a chunk
          // SHIP over the byte bound: a chunk sitting just under LINK_SCORE_CHUNK_MAX_BYTES
          // plus one more large job's bytes still got flushed as ONE oversized request —
          // the check only ever caught the NEXT entry, one job too late. Flush the
          // EXISTING chunk first (without this entry) when either bound would be
          // exceeded, then start a fresh chunk with this entry.
          //
          // A single job that ALONE exceeds LINK_SCORE_CHUNK_MAX_BYTES still ships as
          // its own one-job chunk — `pendingFinish.length > 0` guards the flush-first
          // branch below, so an EMPTY pending chunk never flushes itself just to stay
          // under budget (there is no way to split candidates WITHIN one job without
          // breaking that entry's scoring as a unit). The gateway's own per-string 10KB
          // `validateArgLengths` check is the backstop for this specific edge case (an
          // entry whose search returns enough long candidate names to itself approach
          // ~700KB is not something this chunk boundary can prevent — LINK_SCORE_CHUNK_MAX_BYTES
          // bounds the chunk, not any single job).
          const prospectiveJobBytes = job ? estimateJobBytes(job) : 0;
          if (
            pendingFinish.length > 0 &&
            (pendingFinish.length >= LINK_SCORE_CHUNK_MAX_ENTRIES ||
              chunkBytes + prospectiveJobBytes > LINK_SCORE_CHUNK_MAX_BYTES)
          ) {
            await flushPendingChunk(pendingFinish);
            pendingFinish = [];
            chunkBytes = 0;
          }

          // Everything below depends on this entry's scores (not known until this
          // entry's CHUNK is flushed) — deferred as a closure that captures every local
          // variable it needs (finalEntryId, resolvedSlug, batchEntryWarnings, etc.) by
          // normal lexical scoping, invoked from flushPendingChunk.
          pendingFinish.push({
            entryIdx, entry, resolvedSlug, classifiedBy, confidence, confidenceTier,
            job,
            finish: async (scores: LinkScoreResult[]) => {
              let autoLinkCount = 0;
              // WP-474 E5: auto-links the judge advised but did NOT write (untypeable) — per-entry tally.
              let advisedLinkCount = 0;
              // BET-167: Track overlaps from search results for conflict advisory
              let entryOverlapCount = 0;

              if (candidatesFiltered.length > 0) {
                try {
                  // Server returns results already sorted by score descending; the
                  // `reason` field is a superset this call site simply doesn't consume
                  // (it never carried confidenceReason pre-migration either — no-op for it).
                  const candidates = scores.map((s) => {
                    const r = candidatesFiltered[s.index];
                    return { ...r, collSlug: collIdToSlug.get(r.collectionId) ?? "unknown", confidence: s.score };
                  });

                  // BET-167: Count similar entries for conflict pre-check (reuses search, no extra query)
                  entryOverlapCount = candidates.filter((c) => c.entryId).length;

                  // WP-474 E4-REWIRE: batch auto-links carry provenance + confidence like the single path.
                  const batchAutoLinks: Array<{ fromEntryId: string; toEntryId: string; type: string; proposedBy: string; confidence: number }> = [];
                  for (const c of candidates) {
                    if (batchAutoLinks.length >= MAX_AUTO_LINKS) break;
                    if (c.confidence < AUTO_LINK_CONFIDENCE_THRESHOLD) break;
                    if (!c.entryId) continue;
                    const { type: relationType } = inferRelationType(resolvedSlug!, c.collSlug, profile);
                    batchAutoLinks.push({ fromEntryId: finalEntryId, toEntryId: c.entryId, type: relationType, proposedBy: "auto-link", confidence: c.confidence });
                  }

                  if (batchAutoLinks.length > 0 && preview) {
                    // Finding #13: chain.createEntryRelations (the batch/plural mutation) has
                    // NO preview arg — unlike the singular chain.createEntryRelation used just
                    // below. A previewed createEntry (preview:true, no DB write) was still
                    // followed by a REAL relation write here whenever matching targets existed.
                    // Skip the write entirely in preview mode; report what would happen instead.
                    batchEntryWarnings.push(`${batchAutoLinks.length} auto-link(s) would be created (preview — no DB writes).`);
                  } else if (batchAutoLinks.length > 0) {
                    const batchRes = await kernelMutation<GatewayRouteReturnByName<"chain.createEntryRelations">>("chain.createEntryRelations", {
                      relations: batchAutoLinks, sessionId: agentId ?? undefined,
                    });
                    autoLinkCount = batchRes.created;
                    // WP-474 E5: surface auto-links the judge advised but did not write — never silently dropped.
                    advisedLinkCount = batchRes.advised ?? 0;
                    if (advisedLinkCount > 0) {
                      batchEntryWarnings.push(`${advisedLinkCount} auto-link(s) advised, not written (untypeable) — accept with \`relations action=create\` to promote.`);
                    }
                  }
                } catch { /* batch link failed; entry still created */ }
              }

              // TEN-957 (WP-484 S2): inline per-item relations — validate-all-then-apply
              // (PAT-268 item 1) against the ALREADY-VALIDATED batchCaptureRelationSchema
              // shape (zod validated every item's {to, type} before this handler ran);
              // apply here means "create against the kernel," not "shape validation."
              // Fix 2 (WP-577 review re-review, TEN-2918 follow-up): extracted to
              // lib/batchInlineRelations.ts — a preview batch's source entry was never
              // persisted, so `chain.createEntryRelation` unconditionally threw NOT_FOUND on
              // `fromEntryId` (relations.ts:69-80 resolves it BEFORE any preview branch). See
              // that module's doc comment for the full reasoning.
              const {
                relationsCreatedCount, relationsProposedCount, relationsNotValidatedCount, relationsFailedList,
              } = await applyBatchInlineRelations(entry.relations, finalEntryId, preview, agentId);
              if (relationsFailedList.length > 0) {
                batchEntryWarnings.push(`${relationsFailedList.length} inline relation(s) failed: ${relationsFailedList.join("; ")}`);
              }
              // Fix 4 (WP-577 review round 3, TEN-2918 follow-up): never promise these would be
              // created — target existence and relation policy (`governs` authorization,
              // `guardRelation`) are only checked at real capture, not here.
              if (relationsNotValidatedCount > 0) {
                batchEntryWarnings.push(`${relationsNotValidatedCount} inline relation(s) not validated in preview (target existence and relation policy are checked at capture).`);
              }
              if (relationsProposedCount > 0) {
                batchEntryWarnings.push(`${relationsProposedCount} inline relation(s) converted to an agent proposal (misuse pattern) — review in Cortex UI.`);
              }

              // DEC-896 / BR-76 S3: skip commitEntry when server already returned active.
              // WP-577 / TEN-2918: also skip on `preview` — `autoCommitApplied` doesn't know
              // about preview mode on its own, and without this guard a preview batch capture
              // would fire a REAL `chain.commitEntry` mutation against an entry that was never
              // actually created (createEntry's own preview branch never wrote a doc). This
              // path was unreachable before this slice (preview batch calls always threw
              // ArgumentValidationError before getting this far), so it's a latent bug this
              // fix would otherwise have newly exposed, not a regression in existing behavior.
              if (!preview && autoCommitApplied && !batchWasAutoCommittedServerSide) {
                try {
                  // WP-485 Slice 2b (R2, FEAT-1370): discover, never decide — see the single-entry
                  // auto-commit path above for the full rationale. The enforcement decision now
                  // happens ONE place, server-side, inside commitEntry's gate.
                  const semanticConflicts = await discoverSemanticConflicts(entry.name, entry.description, resolvedSlug);
                  const commitResult = await kernelMutation<GatewayRouteReturnByName<"chain.commitEntry">>("chain.commitEntry", {
                    entryId: finalEntryId,
                    author: agentId ? `agent:${agentId}` : undefined,
                    sessionId: agentId ?? undefined,
                    ...(semanticConflicts.length > 0 ? { conflicts: semanticConflicts } : {}),
                  });
                  const refusalReason = coherencyRefusalReason(commitResult);
                  if (refusalReason) {
                    // WP-465 slice ⑤ (Option B): the entry stays a draft — never accepted/tracked.
                    commitError = `coherency gate refused this accept — ${refusalReason}`;
                    // WP-465 surface parity: thread full refusal payload so routes + mode reach the caller.
                    commitRefusal = commitResultField<Record<string, unknown>>(commitResult, "refusal") ?? undefined;
                    finalStatus = "draft_on_failure";
                  } else {
                    finalStatus = commitResultField<string>(commitResult, "status") === "proposal_created" ? "proposed" : "committed";
                    contradictionAdvisory = commitResultField<ContradictionAdvisory>(commitResult, "contradictionAdvisory") ?? undefined;
                    if (finalStatus === "committed") {
                      await recordSessionActivity({ entryModified: internalId });
                      trackChainEntryCommitted(wsCtx.workspaceId, {
                        entry_id: finalEntryId,
                        collection: resolvedSlug ?? undefined,
                        commit_method: "auto",
                        surface: "mcp_capture",
                      });
                    }
                  }
                } catch (error: unknown) {
                  // BR-76 S2 — DEC-888: Surface failures via log + payload + session TEN. Never silent.
                  commitError = error instanceof Error ? error.message : String(error);
                  finalStatus = "draft_on_failure";
                  await recordCommitFailure({ entryId: finalEntryId, error, sessionId: agentId, server });
                }
              }

              // Server declares `normalization: Record<string, unknown> | null` (looser than
              // this file's own consumption shape) — same documented convention as the
              // single-capture path above (PR #395, Lane C S3b annotation pass).
              const entryNorm = result.normalization as
                | { remapped: Record<string, string>; rejected: string[] }
                | null
                | undefined;
              results.push({
                entryIdx,
                name: entry.name,
                // P0 round 4 (fix 1/3, root): EFFECTIVE (post-routing) collection — see
                // `effectiveCollection`'s declaration above, ~2 screens up.
                collection: effectiveCollection,
                entryId: finalEntryId,
                ok: true,
                autoLinks: autoLinkCount,
                advisedLinks: advisedLinkCount,
                status: finalStatus,
                classifiedBy,
                confidence,
                confidenceTier,
                ...(previewWouldBlock ? { wouldBlock: previewWouldBlock.wouldBlock } : {}),
                ...(previewWouldBlock?.blockingReason ? { blockingReason: previewWouldBlock.blockingReason } : {}),
                // Fix 1 (TEN-2918 follow-up): not caller-supplied — the preview projection
                // must not report this predicted-but-duplicable ID as concrete.
                ...(!entry.entryId ? { entryIdAssignedAtCapture: true } : {}),
                ...(commitError ? { commitError } : {}),
                // WP-465 surface parity: thread full refusal payload for coherency-refused batch entries.
                ...(commitRefusal ? { commitRefusal } : {}),
                // WP-485 Slice 2b (R2, FEAT-1370): the server's contradiction advisory (DEC-1321 —
                // advisory only, never blocking) — visible, never a silent success.
                ...(contradictionAdvisory ? { contradictionAdvisory } : {}),
                ...(batchEntryWarnings.length > 0 ? { warnings: batchEntryWarnings } : {}),
                ...(entryNorm && (Object.keys(entryNorm.remapped).length > 0 || entryNorm.rejected.length > 0) && {
                  normalization: { remapped: entryNorm.remapped, rejected: entryNorm.rejected },
                }),
                // BET-167: Track overlap count for conflict advisory
                ...(entryOverlapCount > 0 ? { overlapCount: entryOverlapCount } : {}),
                // TEN-2365: capture-time authority-domain proposal echo.
                ...(result.authorityDomain ? { authorityDomain: result.authorityDomain } : {}),
                // TEN-957 (WP-484 S2): inline relations created for this entry.
                ...(relationsCreatedCount > 0 ? { relationsCreated: relationsCreatedCount } : {}),
                // Finding #14: distinct from relationsCreated — never counted as a write.
                ...(relationsProposedCount > 0 ? { relationsProposed: relationsProposedCount } : {}),
              });
            },
          });

          // This entry's job is now part of the (possibly just-flushed-and-fresh)
          // current chunk — track its bytes for the NEXT entry's before-append check
          // above. Flushing happens inside the loop (the check above, before push), so
          // chunk N+1's `createEntry` always waits for chunk N's `finish` (relation
          // writes) to complete first — the for-loop is sequential and that flush call
          // is awaited.
          if (job) chunkBytes += prospectiveJobBytes;
        } catch (error: unknown) {
          results.push(buildBatchEntryFailure({
            entryIdx, name: entry.name, collection: resolvedSlug, classifiedBy, confidence, confidenceTier,
            ...describeBatchEntryOutcome(error, entry.name), // WP-575 review (P1, TEN-1126) — see lib/batchTimeoutCohort.ts.
          }));
        }
      }

      // Flush any remaining partial chunk (fewer than LINK_SCORE_CHUNK_MAX_ENTRIES
      // entries, under the byte bound) once the loop finishes.
      await flushPendingChunk(pendingFinish);

      const created = results.filter((r) => r.ok);
      const { failed, unknownOutcome: unknownOutcomeEntries } = splitBatchNotOk(results.filter((r) => !r.ok)); // WP-575 review (P1, TEN-1126) — see lib/batchTimeoutCohort.ts.
      const committed = created.filter((r) => r.status === "committed");
      const proposed = created.filter((r) => r.status === "proposed");
      const drafts = created.filter((r) => r.status === "draft");
      const commitFailed = created.filter((r) => r.status === "draft_on_failure");
      const classifiedCount = created.filter((r) => r.classifiedBy && r.classifiedBy !== "explicit").length;

      await server.sendLoggingMessage({
        level: "info",
        data: `Batch complete. ${created.length} succeeded, ${failed.length} failed, ${skippedLowConfidence.length} skipped (low confidence).`,
        logger: "product-brain",
      });

      // WP-577 / TEN-2918 follow-up: everything below builds the REAL-create surface —
      // "# Batch Capture Complete", per-entry `status: "draft"`, and a `next` offering
      // `entries action=commit` on an entryId a preview never wrote. handleCapture's
      // single path returns a dedicated preview envelope for exactly this reason; batch
      // had none, so a preview batch reported itself as a successful capture. Return the
      // honest envelope here, before any of that shaping. See lib/batchCaptureOutput.ts.
      // WP-577 review follow-up (P2, TEN-2918): cohort-splitting + mapping (wouldCapture vs.
      // wouldBlock, failed, skipped) now lives in buildBatchPreviewFromResults, alongside the
      // preview contract it feeds (lib/batchCaptureOutput.ts) — STD-2 / DEC-1504 shrink-only.
      if (preview) {
        return buildBatchPreviewFromResults(created, failed, skippedLowConfidence, entries.length, entries, autoCommit);
      }

      const totalAutoLinks = created.reduce((sum, r) => sum + r.autoLinks, 0);
      // WP-474 E5: aggregate advised-not-written cohort across the batch — never silently dropped.
      const totalAdvisedLinks = created.reduce((sum, r) => sum + r.advisedLinks, 0);

      const byCollection = new Map<string, number>();
      for (const r of created) {
        byCollection.set(r.collection, (byCollection.get(r.collection) ?? 0) + 1);
      }

      const lines: string[] = [
        `# Batch Capture Complete`,
        `**${created.length}** created, **${failed.length}** failed, **${skippedLowConfidence.length}** skipped out of ${entries.length} total.`,
        `**Auto-links created:** ${totalAutoLinks}${totalAdvisedLinks > 0 ? `, ${totalAdvisedLinks} advised` : ""}`,
        "",
      ];

      if (classifiedCount > 0) {
        lines.push(`**Auto-classified:** ${classifiedCount} entries routed by LLM/heuristic.`);
        lines.push("");
      }

      if (created.length > 0) {
        const commitFailedSegment = commitFailed.length > 0 ? `, ${commitFailed.length} commit-failed` : "";
        lines.push(
          `**Statuses:** ${committed.length} committed, ${proposed.length} proposed, ${drafts.length} draft${commitFailedSegment}.`,
        );
        lines.push("");
      }

      if (byCollection.size > 0) {
        lines.push("## By Collection");
        for (const [col, count] of byCollection) {
          lines.push(`- \`${col}\`: ${count} entries`);
        }
        lines.push("");
      }

      if (created.length > 0) {
        const highConfidence = created.filter((r) => r.confidenceTier === "high" || r.classifiedBy === "explicit");
        const mediumConfidence = created.filter((r) => r.confidenceTier === "medium");

        if (highConfidence.length > 0) {
          lines.push("## Created — High Confidence / Explicit");
          for (const r of highConfidence) {
            const linkNote = r.autoLinks > 0 ? `, ${r.autoLinks} auto-links` : "";
            const classNote = r.classifiedBy !== "explicit" ? ` (${r.classifiedBy} ${r.confidence}%)` : "";
            lines.push(`- **${r.entryId}**: ${r.name} [${r.collection}] — \`${r.status}\`${classNote}${linkNote}`);
          }
        }

        if (mediumConfidence.length > 0) {
          lines.push("");
          lines.push("## Created — Medium Confidence (review recommended)");
          for (const r of mediumConfidence) {
            const linkNote = r.autoLinks > 0 ? `, ${r.autoLinks} auto-links` : "";
            lines.push(`- **${r.entryId}**: ${r.name} [${r.collection}] — \`${r.status}\` (${r.classifiedBy} ${r.confidence}%)${linkNote}`);
          }
          lines.push(`\n_Use \`entries action=move\` to correct any misclassified entries._`);
        }
      }

      if (skippedLowConfidence.length > 0) {
        lines.push("");
        lines.push("## Skipped — Low Confidence (needs explicit collection)");
        for (const s of skippedLowConfidence) {
          const suggestion = s.suggestedCollection
            ? ` — best guess: \`${s.suggestedCollection}\` (${s.confidence}%)`
            : " — no classification available";
          const alts = s.alternatives?.length
            ? ` | alternatives: ${s.alternatives.map((a) => `\`${a.collection}\` (${a.confidence}%)`).join(", ")}`
            : "";
          lines.push(`- **[${s.index}]** ${s.name}${suggestion}${alts}`);
        }
        lines.push("");
        lines.push("_Re-capture these with an explicit `collection` or use the suggested collection._");
      }

      if (commitFailed.length > 0) {
        lines.push("");
        lines.push("## Saved as draft");
        for (const r of commitFailed) {
          if (r.commitRefusal) {
            // WP-465 surface parity: mirror the single path — the refusal block already leads with
            // the reason, so DROP commitError from the bullet (it embeds the same reason → no double
            // print). Render the block at top level (NOT indented — indenting turns the `##`/`###`
            // headings into a code block) and demote the leading heading to H3.
            lines.push(`- **${r.entryId}**: ${r.name} [${r.collection}]`);
            const refusalText = coherencyRefusedResult("batch-capture", r.entryId ?? "", r.commitRefusal, { headingLevel: 3 }).content[0].text;
            lines.push(...refusalText.split("\n"));
          } else {
            lines.push(`- **${r.entryId}**: ${r.name} [${r.collection}] — ${r.commitError}`);
          }
        }
      }

      // WP-485 Slice 2b round 2 (Codex P1, FEAT-1370): surface the server's per-entry
      // contradiction advisory in the batch response text — a high-confidence contradiction
      // committing with no visible trace is exactly the silent-success failure R2 exists to
      // prevent (mirrors the single-entry "## Contradiction advisory" section above).
      const entriesWithContradictionAdvisory = created.filter((r) => r.contradictionAdvisory);
      if (entriesWithContradictionAdvisory.length > 0) {
        lines.push("");
        lines.push("## Contradiction Advisories");
        for (const r of entriesWithContradictionAdvisory) {
          lines.push(`- **${r.entryId}**: ${r.name} — ${r.contradictionAdvisory!.reason}`);
          for (const c of r.contradictionAdvisory!.conflictDetails) {
            lines.push(`  - \`${c.chainEntryId}\` ${c.chainEntryName}: ${c.explanation}`);
          }
        }
      }

      // BET-167: Advisory overlap notice for batch entries with potential duplicates
      if (autoCommitApplied) {
        const entriesWithOverlaps = created.filter((r) => r.overlapCount && r.overlapCount > 0);
        if (entriesWithOverlaps.length > 0) {
          lines.push("");
          lines.push("## Potential Overlaps");
          lines.push(`${entriesWithOverlaps.length} ${entriesWithOverlaps.length === 1 ? "entry has" : "entries have"} similar existing entries. Consider reviewing for duplicates:`);
          for (const r of entriesWithOverlaps) {
            lines.push(`- **${r.entryId}**: ${r.name} — ${r.overlapCount} similar ${r.overlapCount === 1 ? "entry" : "entries"}`);
          }
        }
      }

      const entriesWithWarnings = created.filter((r) => r.warnings && r.warnings.length > 0);
      if (entriesWithWarnings.length > 0) {
        lines.push("");
        lines.push("## Validation Warnings");
        for (const r of entriesWithWarnings) {
          lines.push(`- **${r.entryId}** (${r.name}): ${r.warnings!.join("; ")}`);
        }
      }

      if (failed.length > 0) {
        lines.push("");
        lines.push("## Failed");
        for (const r of failed) {
          lines.push(`- ${r.name} [${r.collection}]: _${r.error}_`);
        }
        lines.push("");
        lines.push(`_If failed > 0, inspect \`failedEntries\` in the structured response and retry individually._`);
      }
      lines.push(...renderOutcomeUnknownSection(unknownOutcomeEntries)); // WP-575 review (P1, TEN-1126) — see lib/batchTimeoutCohort.ts.
      const entryIds = created.map((r) => r.entryId);
      if (entryIds.length > 0 || skippedLowConfidence.length > 0) {
        lines.push("");
        lines.push("## Next Steps");
        if (entryIds.length > 0) {
          lines.push(`- **Connect:** Run \`relations action=suggest\` on key entries to build the knowledge graph`);
        }
        if (drafts.length > 0) {
          lines.push(`- **Accept:** Use \`entries action=commit\` to promote remaining drafts to SSOT`);
        }
        if (skippedLowConfidence.length > 0) {
          lines.push(`- **Classify:** Re-capture ${skippedLowConfidence.length} skipped entries with explicit collections`);
        }
        if (entryIds.length > 0) {
          lines.push(`- **Quality:** Run \`quality action=check\` on individual entries to assess completeness`);
        }
      }

      const skippedNote = skippedLowConfidence.length > 0 ? `, ${skippedLowConfidence.length} skipped (low confidence)` : "";
      const classifiedNote = classifiedCount > 0 ? `, ${classifiedCount} auto-classified` : "";
      const commitFailedNote = commitFailed.length > 0 ? `, ${commitFailed.length} commit-failed` : "";
      const summary =
        failed.length > 0 || unknownOutcomeEntries.length > 0 || skippedLowConfidence.length > 0 || commitFailed.length > 0
          ? `Batch captured ${created.length}/${entries.length} entries (${failed.length} failed${unknownOutcomeSummaryNote(unknownOutcomeEntries)}${skippedNote}, ${committed.length} committed, ${proposed.length} proposed, ${drafts.length} draft${commitFailedNote}${classifiedNote}).`
          : `Batch captured ${created.length} entries successfully (${committed.length} committed, ${proposed.length} proposed, ${drafts.length} draft${classifiedNote}).`;

      const firstDraft = drafts[0];

      const next: NextAction[] = [];
      if (created.length > 0) {
        next.push({ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId: created[0].entryId } });
      }
      if (firstDraft) {
        next.push({ tool: "entries" as const, description: "Accept first draft", parameters: { action: "commit", entryId: firstDraft.entryId } });
      }
      if (skippedLowConfidence.length > 0) {
        next.push({ tool: "capture", description: `Capture skipped entry with explicit collection`, parameters: { name: skippedLowConfidence[0].name, collection: skippedLowConfidence[0].suggestedCollection ?? "" } });
      }

      const batchTotalMs = Date.now() - batchTimingStart;
      const batchTiming = {
        totalMs: batchTotalMs,
        perEntryAvgMs: created.length > 0 ? Math.round(batchTotalMs / created.length) : 0,
      };

      return {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: {
          ...success(
            summary,
            {
              captured: created.map((r) => ({
              entryId: r.entryId,
              collection: r.collection,
              name: r.name,
              status: r.status,
              ...(r.classifiedBy ? { classifiedBy: r.classifiedBy } : {}),
              ...(r.confidence != null ? { confidence: r.confidence } : {}),
              ...(r.confidenceTier ? { confidenceTier: r.confidenceTier } : {}),
              ...(r.warnings?.length ? { warnings: r.warnings } : {}),
              ...(r.normalization ? { normalization: r.normalization } : {}),
              ...(r.commitError ? { commitError: r.commitError } : {}),
              // WP-465 surface parity: thread full refusal payload into batch structured output.
              ...(r.commitRefusal ? { coherencyRefusal: r.commitRefusal } : {}),
              ...(r.authorityDomain ? { domain: r.authorityDomain.slug } : {}),
              // TEN-957 (WP-484 S2): inline relations created for this entry.
              ...(r.relationsCreated ? { relationsCreated: r.relationsCreated } : {}),
              // Finding #14: proposal outcomes are distinct from writes — never folded in.
              ...(r.relationsProposed ? { relationsProposed: r.relationsProposed } : {}),
              // WP-485 Slice 2b round 2 (Codex P1): the server's per-entry contradiction
              // advisory — was reaching `results` but dropped here, the final projection the
              // caller actually receives. Visible, never a silent success.
              ...(r.contradictionAdvisory ? { contradictionAdvisory: r.contradictionAdvisory } : {}),
            })),
            total: created.length,
            failed: failed.length,
            committed: committed.length,
            proposed: proposed.length,
            drafts: drafts.length,
            ...(commitFailed.length > 0 ? { commitFailed: commitFailed.length } : {}),
            ...(classifiedCount > 0 ? { classified: classifiedCount } : {}),
            autoCommitApplied,
            ...(skippedLowConfidence.length > 0 && {
              skippedLowConfidence: skippedLowConfidence.map((s) => ({
                index: s.index,
                name: s.name,
                ...(s.suggestedCollection ? { suggestedCollection: s.suggestedCollection } : {}),
                ...(s.confidence != null ? { confidence: s.confidence } : {}),
                ...(s.alternatives?.length ? { alternatives: s.alternatives } : {}),
              })),
            }),
            ...(failed.length > 0 && {
              // PR #392 round 2 (Codex, finding 2): `r.entryIdx` is the ORIGINAL input
              // position, carried explicitly since the moment each result was built —
              // NEVER `results.indexOf(r)` (the chunked pipeline pushes results in a
              // different order than `entries`' input order, so that would silently
              // report the wrong index for a batch with a mix of early/late failures).
              failedEntries: failed.map((r) => ({
                index: r.entryIdx,
                collection: r.collection,
                name: r.name,
                error: r.error ?? "unknown error",
              })),
            }), ...(unknownOutcomeEntries.length > 0 && { unknownOutcomeEntries }), // WP-575 review (P1, TEN-1126) — kept OUT of failedEntries.
          },
          next,
        ),
          _meta: { timing: batchTiming },
        },
      };
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * WP-465 slice ⑤ (Option B): `chain.commitEntry` RETURNS a structured coherency refusal
 * instead of throwing. Detect it so an auto-commit refusal is surfaced as a non-committed
 * draft (mirrors the thrown-failure path) and is NEVER reported or tracked as accepted.
 * Returns the refusal reason when refused, otherwise null.
 */
function coherencyRefusalReason(commitResult: unknown): string | null {
  // `chain.commitEntry`'s real return is a discriminated union whose plain-success branch
  // has NO `status` field at all (not even `status: undefined`) — accepting `unknown` and
  // narrowing with `in` here (PR #395, Lane C S3b annotation pass) matches every branch
  // shape without a hand-written parameter type drifting from the server's real union.
  if (
    !commitResult ||
    typeof commitResult !== "object" ||
    !("status" in commitResult) ||
    (commitResult as { status?: unknown }).status !== "coherency_refused"
  ) {
    return null;
  }
  const refusal =
    "refusal" in commitResult ? (commitResult as { refusal?: { reason?: string } }).refusal : undefined;
  return refusal?.reason ?? "refused by the coherency gate";
}

/**
 * Safe field reads across `chain.commitEntry`'s discriminated union (see
 * `coherencyRefusalReason` above for why the plain-success branch has no shared fields
 * with the others) — used at both call sites below (PR #395, Lane C S3b annotation pass).
 */
function commitResultField<T>(commitResult: unknown, field: string): T | undefined {
  if (!commitResult || typeof commitResult !== "object" || !(field in commitResult)) return undefined;
  return (commitResult as Record<string, unknown>)[field] as T | undefined;
}

/**
 * BR-76 S2 — DEC-888: Surface commitEntry failures so they are never silent.
 * Fire-and-forget: each fan-out step has its own try/catch so a failure in one
 * does NOT block the others and does NOT change the caller's finalStatus.
 *
 * Steps:
 *  1. server.sendLoggingMessage (level "error") — visible in MCP transcript stream.
 *  2. Session-bound TEN emit via chain.createEntry — persisted to Chain when an
 *     active write session (agentId) exists.
 *
 * Persistence to a queryable DB field is deferred (TEN-1811 follow-on).
 */
async function recordCommitFailure({
  entryId,
  error,
  sessionId,
  server,
}: {
  entryId: string;
  error: unknown;
  sessionId: string | null | undefined;
  server: McpServer;
}): Promise<void> {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const logLine = `commitEntry failed for ${entryId}: ${errorMessage}${sessionId ? ` (session: ${sessionId})` : ""}`;

  // Step 1: MCP transcript log — level "error"
  try {
    await server.sendLoggingMessage({ level: "error", data: logLine, logger: "product-brain" });
  } catch {
    // Logging must never break the caller
  }

  // Step 2: Session-bound TEN emit — only when an active write session exists
  if (sessionId) {
    try {
      const tenName = `commitEntry failed for ${entryId} — ${errorMessage}`.slice(0, 250);
      await kernelMutation<GatewayRouteReturnByName<"chain.createEntry">>("chain.createEntry", {
        collectionSlug: "tensions",
        name: tenName,
        // Failure audit TENs intentionally stay draft — they need explicit human review,
        // not auto-commit, even in Open mode.
        status: "draft",
        data: {},
        createdBy: `agent:${sessionId}`,
        sessionId,
      });
    } catch {
      // TEN emit is advisory — never propagates
    }
  }
}

// STOP_WORDS/tokenizeText moved to ../lib/textMatch.ts (STD-2 / DEC-1504 module-health
// extraction) — imported above; behavior unchanged.

/**
 * Keyword contradiction check against governance entries.
 * Extracts terms 4+ chars, searches architecture and business-rules,
 * checks for 'governs' relations, returns formatted warnings.
 * Non-blocking — never throws, never prevents the operation.
 */
export interface ContradictionWarning {
  entryId: string;
  name: string;
  collection: string;
  governsCount: number;
}

export async function runContradictionCheck(
  name: string,
  description: string,
): Promise<ContradictionWarning[]> {
  const warnings: ContradictionWarning[] = [];
  try {
    const keyTerms = tokenizeText(`${name} ${description}`)
      .filter((w) => w.length >= 4 && !STOP_WORDS.has(w))
      .slice(0, 8);

    if (keyTerms.length === 0) return warnings;

    const searchQuery = keyTerms.slice(0, 5).join(" ");
    const [govResults, archResults] = await Promise.all([
      kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query: searchQuery, collectionSlug: "business-rules" }),
      kernelQuery<GatewayRouteReturnByName<"chain.searchEntries">>("chain.searchEntries", { query: searchQuery, collectionSlug: "architecture" }),
    ]);

    const allGov = [...(govResults ?? []), ...(archResults ?? [])].slice(0, 5);

    for (const entry of allGov) {
      const entryTokens = new Set(tokenizeText(`${entry.name} ${entry.data?.description ?? ""}`));
      const matched = keyTerms.filter((t) => entryTokens.has(t));
      if (matched.length < 3) continue;

      // Check for 'governs' relations
      let governsCount = 0;
      try {
        const relations = await kernelQuery<GatewayRouteReturnByName<"chain.listEntryRelations">>("chain.listEntryRelations", {
          entryId: entry.entryId,
        });
        governsCount = (relations ?? []).filter((r: any) => r.type === "governs").length;
      } catch { /* non-critical */ }

      warnings.push({
        entryId: entry.entryId ?? "",
        name: entry.name,
        collection: entry.collectionSlug ?? "",
        governsCount,
      });
    }
  } catch {
    // Contradiction check is advisory — never blocks the operation
  }
  return warnings;
}

interface SemanticConflictResult {
  chainEntryId: string;
  chainEntryName: string;
  chainEntryDescription?: string;
  type: "contradiction" | "supersession" | "duplicate" | "overlap";
  confidence: number;
  explanation: string;
}

/**
 * WP-485 Slice 2b (R2, FEAT-1370): the typed advisory chain.commitEntry's contradiction gate
 * attaches to a success response when the caller-supplied `conflicts` include a high-confidence
 * contradiction (DEC-1321 — advisory only, never blocking). Mirrors the shape
 * convex/agentKnowledge/entries.ts's `commitEntry` returns.
 */
interface ContradictionAdvisory {
  reason: string;
  blockingCount: number;
  conflictDetails: SemanticConflictResult[];
}

/**
 * WP-485 Slice 2b (R2, FEAT-1370): discover semantic conflicts to hand to chain.commitEntry's
 * CALLER-FED `conflicts` arg. Previously this file ran its own private
 * BLOCKING_CONTRADICTION_THRESHOLD comparison and threw a local `commitError` before ever
 * calling commitEntry — a THIRD independent copy of the enforcement decision, distinct from
 * conflict-preflight.ts's own copy. Deleted: the enforcement decision is now made ONE place,
 * server-side, by commitEntry's evaluate()/assessContradiction gate (Slice 2a). This helper only
 * DISCOVERS candidates — it never decides whether to block.
 */
async function discoverSemanticConflicts(
  name: string,
  description: string,
  collectionHint?: string,
): Promise<SemanticConflictResult[]> {
  try {
    const conflicts = await kernelQuery<GatewayRouteReturnByName<"chain.detectSemanticConflicts">>("chain.detectSemanticConflicts", {
      name,
      description,
      ...(collectionHint ? { collectionHint } : {}),
    });
    // WP-485 Slice 2b (live-proof finding): `chain.detectSemanticConflicts` also returns
    // `chainEntryDocId` (convex/intelligence/conflictDetection.ts DetectResult) — an internal
    // doc id NOT declared on `semanticConflictValidator` (convex/lib/conflictPolicy.ts), the
    // validator for commitEntry's `conflicts` arg. Passing the raw result straight through as
    // `conflicts` fails Convex's strict argument validation (caught live: a real detected
    // contradiction threw ArgumentValidationError). Whitelist only the accepted fields — both
    // call sites below hand this return value straight to chain.commitEntry.
    return (conflicts ?? []).map((c) => ({
      chainEntryId: c.chainEntryId,
      chainEntryName: c.chainEntryName,
      ...(c.chainEntryDescription ? { chainEntryDescription: c.chainEntryDescription } : {}),
      type: c.type,
      confidence: c.confidence,
      explanation: c.explanation,
    }));
  } catch {
    return [];
  }
}

// ── Quality Coaching Helpers ──────────────────────────────────────────────

// WP-475 E1 (Copilot review): typed structural subset of the kernel QualityVerdict
// (kernel/quality/types.ts) — exactly the fields this tool consumes (render via
// formatRubricCoaching, telemetry via trackQualityVerdict, envelope qualityVerdict).
// Precedent: the CLI's local mirror in packages/cli/src/formatters/coaching.ts.
// The runtime value may carry more fields; they pass through the envelope spread
// untouched.
interface CoachingCriterionSubset {
  id: string;
  passed: boolean;
  hint: string;
  questionTemplate: string;
}

interface CoachingVerdictSubset {
  canonicalKey: string;
  tier: string;
  passed: boolean;
  criteria: CoachingCriterionSubset[];
  weakest: { criterion: string; hint: string; questionTemplate: string } | null;
  /** WP-475 E3: rubric methodology that produced the verdict (v1-vs-v2 seam). */
  methodologyVersion?: string;
}

interface CoachingResultSubset {
  verdict: CoachingVerdictSubset;
  source: string;
}

// Round-3 review (TEN-2918 follow-up): moved to lib/rubricFormatting.ts — genuine cohesive
// extraction that pays for this round's additions elsewhere in this shrink-only file (STD-2 /
// DEC-1504). Imported (not just re-exported) because formatRubricCoaching/verdictQualityPhrase
// are also called from within this file; re-exported so knowledge.ts/quality.ts/tests importing
// them from "../tools/smart-capture.js" keep working unmodified.
export { verdictQualityPhrase, formatRubricCoaching, formatRubricVerdictSection };
